import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, APP_INITIALIZER, NgModule, Optional } from '@angular/core';
import * as i2 from '@ngrx/store';
import { createFeatureSelector, createSelector, createAction, props, createReducer, on, StoreModule } from '@ngrx/store';
import * as i1 from '@angular/router';
import { convertToParamMap, NavigationStart, NavigationEnd } from '@angular/router';
import { of, BehaviorSubject, iif, timer, throwError, forkJoin, from, Subject, combineLatest, merge, Observable } from 'rxjs';
import jwt_decode from 'jwt-decode';
import { retryWhen, concatMap, distinctUntilChanged, take, map, startWith, mergeMap, filter, first, pairwise, tap, switchMap, finalize, shareReplay, catchError, withLatestFrom } from 'rxjs/operators';
import * as LogRocket from 'logrocket';
import LogRocket__default from 'logrocket';
import { Network } from '@capacitor/network';
import * as i2$1 from '@panamax/browser-storage';
import { Device } from '@capacitor/device';
import * as i1$1 from '@ionic/angular';
import * as i1$2 from '@splitsoftware/splitio-angular';
import { Preferences } from '@capacitor/preferences';
import { WebTracerProvider } from '@opentelemetry/web';
import { ZoneContextManager } from '@opentelemetry/context-zone-peer-dep';
import { CollectorTraceExporter } from '@opentelemetry/exporter-collector';
import { Resource } from '@opentelemetry/resources';
import { BatchSpanProcessor } from '@opentelemetry/tracing';
import { HttpTraceContextPropagator } from '@opentelemetry/core';
import * as api from '@opentelemetry/api';
import { context, trace, TraceFlags, SpanStatusCode } from '@opentelemetry/api';
import * as i1$3 from '@angular/common';
import { Capacitor } from '@capacitor/core';
import * as i1$4 from '@angular/common/http';
import { HttpHeaders } from '@angular/common/http';
import { v4 } from 'uuid';
import { UserKinds } from '@usf/user-types/user';
import { IonicAuth } from '@ionic-enterprise/auth';
import * as i1$5 from '@ngrx/effects';
import { createEffect, ofType, EffectsModule } from '@ngrx/effects';
import * as i2$2 from '@panamax/websocket';
import LogrocketFuzzySanitizer from 'logrocket-fuzzy-search-sanitizer';

class MockRouter {
    constructor() {
        this.events = of('test');
        this.url = '';
    }
    navigate(commands, extras) { }
    navigateByUrl(commands, extras) { }
    parseUrl(url) { }
}
class MockActivatedRoute {
    constructor(params, data) {
        this.subject = new BehaviorSubject(this.testParams);
        this.dataSubject = new BehaviorSubject(this.testData);
        this.params = this.subject.asObservable();
        this.queryParams = this.subject.asObservable();
        this.data = this.dataSubject.asObservable();
        if (params) {
            this.testParams = params;
            this.paramMap = convertToParamMap(params);
        }
        else {
            this.testParams = {};
        }
        if (data) {
            this.testData = data;
        }
        else {
            this.testData = {};
        }
    }
    get testParams() {
        return this.innerTestParams;
    }
    set testParams(params) {
        this.innerTestParams = params;
        this.subject.next(params);
    }
    get testData() {
        return this.innerData;
    }
    set testData(data) {
        this.innerData = data;
        this.subject.next(data);
    }
    get snapshot() {
        return {
            params: this.testParams,
            queryParams: this.testParams,
            paramMap: this.paramMap,
            data: this.testData,
        };
    }
}
// make the async functions safe in unit tests
// by handling error.
const makeAsyncSafe = (fn, errorHandler) => {
    return () => {
        fn().catch(errorHandler);
    };
};
const handleUnitTestError = (err) => {
    console.log('Handing async unit test error: ', err);
};

const jwtDecode = (token) => {
    try {
        return jwt_decode(token);
    }
    catch (Error) {
        return null;
    }
};
const isTokenExpired = (token = '') => {
    const decoded = jwtDecode(token);
    if (decoded) {
        return decoded.exp * 1000 < new Date().getTime();
    }
    else {
        return true;
    }
};

const defaultApiRetryStrategy = {
    initialInterval: 1000,
    maxRetries: 3,
    maxInterval: 10000,
    shouldRetry: (error) => defaultApiRetryConditions(error),
};
const defaultApiRetryConditions = (error) => {
    const retryCondition = error.status >= 500 || (error.ok === false && error.status === 0);
    if (retryCondition) {
        console.warn('Default API Retry Error Condition', error);
    }
    return retryCondition;
};
function getReferenceFromString(rootObject, nestedAttributes) {
    const derivedObject = nestedAttributes
        .split('.')
        .reduce((buildObject, thisAttribute) => {
        return buildObject[thisAttribute];
    }, rootObject);
    return isNaN(derivedObject) ? derivedObject : Number(derivedObject);
}
function retryBackoff(config) {
    const { initialInterval, maxRetries = Infinity, maxInterval = Infinity, shouldRetry = () => true, backoffDelay = exponentialBackoffDelay, } = typeof config === 'number' ? { initialInterval: config } : config;
    return (source) => source.pipe(retryWhen((errors) => errors.pipe(concatMap((error, i) => iif(() => {
        return i < maxRetries && shouldRetry(error);
    }, timer(getDelay(backoffDelay(i, initialInterval), maxInterval)), throwError(error))))));
}
function getDelay(backoffDelay, maxInterval) {
    return Math.min(backoffDelay, maxInterval);
}
function exponentialBackoffDelay(iteration, initialInterval) {
    return Math.pow(2, iteration) * initialInterval;
}

const selectAppState = createFeatureSelector('app-state');
const selectToken = () => createSelector(selectAppState, (state) => state?.token);
const selectAccessToken = () => createSelector(selectAppState, (state) => state?.token?.accessToken);
const selectRefreshToken = () => createSelector(selectAppState, (state) => state?.token?.refreshToken);
const selectTokenExpiration = () => createSelector(selectAppState, (state) => state?.token?.expiresIn);
const selectCustomer = () => createSelector(selectAppState, (state) => state?.selectedCustomer);
const selectOnline = () => createSelector(selectAppState, (state) => state?.online);
const selectPageTitle = () => createSelector(selectAppState, (state) => state?.pageTitle);
const selectCurrentUrl = () => createSelector(selectAppState, (state) => state?.currentUrl);
const selectPreviousUrl = () => createSelector(selectAppState, (state) => state?.previousUrl);
const selectLogrocketUrl = () => createSelector(selectAppState, (state) => state?.logrocketUrl);
const selectPlatform = () => createSelector(selectAppState, (state) => state?.platform);
const selectPlatformType = () => createSelector(selectPlatform(), (platform) => platform?.platformType);
const selectLoggedInUser = () => createSelector(selectAppState, (state) => state?.loggedInUser);
const selectUserProfiles = () => createSelector(selectAppState, (state) => state?.loggedInUser.profiles);
const selectShowInventory = createSelector(selectAccessToken(), (token) => {
    try {
        const decodedToken = jwtDecode(token);
        if (!decodedToken) {
            return false;
        }
        else {
            if (decodedToken['usf-claims']?.hideInventory === 'Y')
                return false;
            return true;
        }
    }
    catch (Error) {
        return false;
    }
});
const selectOriginType = () => createSelector(selectAppState, (state) => state?.originType);
const selectLinkSource = () => createSelector(selectAppState, (state) => state?.linkSource);
const selectRank = () => createSelector(selectAppState, (state) => state?.rank);
const selectSession = () => createSelector(selectAppState, (state) => state?.session);
const selectMinimumVersionsLoaded = () => createSelector(selectAppState, (state) => state?.minimumVersionsLoaded);
const selectMinimumVersions = () => createSelector(selectAppState, (state) => state?.minimumVersions);
/**
 * @deprecated use PanAppState.feature$ instead
 */
const selectAllFeaturesEnabled = (features) => createSelector(selectAppState, (state) => {
    if (state?.features) {
        return features.every((feature) => {
            const enabled = state.features[feature];
            return !!enabled;
        });
    }
    else {
        return false;
    }
});
const selectBackgroundTime = createSelector(selectAppState, (state) => state?.backgroundTime);

const TOKEN_ACTIONS = {
    /**
     * @deprecated Use startSession instead
     */
    loadTokenSuccess: createAction('[PanAppState - Token] USF Tokens Loaded Effect', props()),
    loadAccessTokenSuccess: createAction('[PanAppState - Token] Access Token Loaded', props()),
    loadRefreshTokenSuccess: createAction('[PanAppState - Token] Refresh Token Loaded', props()),
    loadTokenExirationSuccess: createAction('[PanAppState - Token] Token Expiration Loaded', props()),
    /**
     * @deprecated replaced by saveToken
     */
    loadTokenStateSuccess: createAction('[PanAppState - Token] USF Tokens Loaded Successfully', props()),
    loadTokenFail: createAction('[PanAppState - Token] Token Error'),
    clearTokenState: createAction('[PanAppState - Token] Clear Token State'),
    clearStore: createAction('[PanAppState - Token] Clear App State'),
    saveToken: createAction('[Token] Save Token', props()),
};

const CUSTOMER_ACTIONS = {
    loadSelectedCustomerSuccess: createAction('[PanAppState - Customer] Selected Customer Loaded', props())
};

const USER_ACTIONS = {
    loadUser: createAction('[PanAppState - User] Loading User'),
    loadUserSuccess: createAction('[PanAppState - User] User Loaded', props()),
    loadUserFail: createAction('[PanAppState - User] Loading User Failed'),
    loadAssignedCustomers: createAction('[PanAppState - User] Assigned Customers Persisted', props()),
    loadProfileSettings: createAction('[PanAppState - User] Profile Settings Persisted', props())
};

const NETWORK_ACTIONS = {
    setStatus: createAction('[PanAppState - Network] Online/Offline Status Change', props()),
    setOnline: createAction('[PanAppState - Network] Connected'),
    setOffline: createAction('[PanAppState - Network] Disconnected')
};

const PAGE_ACTIONS = {
    setPageTitle: createAction('[PanAppState - Page] Set Page Title', props()),
    setCurrentUrl: createAction('[PanAppState - Page] Set Current Url', props()),
    setPreviousUrl: createAction('[PanAppState - Page] Set Previous Url', props()),
    setOriginType: createAction('[PanAppState - Page] Set Origin type', props()),
    setLinkSource: createAction('[PanAppState - Page] Set Link Source', props()),
    setLogrocketUrl: createAction('[PanAppState - Page] Set Logrocket Url', props()),
    setRank: createAction('[PanAppState - Page] Set Rank', props()),
};

const PLATFORM_ACTIONS = {
    setPlatform: createAction('[PanAppState - Platform] Save Platform Details', props()),
    setBrowserDimensions: createAction('[PanAppState - Platform] Save Browser Dimensions', props()),
    appForeground: createAction(
    //add time in milliseconds since last foregrounded
    '[PanAppState - Platform] App entered foreground', props()),
    appBackground: createAction('[PanAppState - Platform] App entered background', props()),
    /**
     * @description Used for setting the UserState i.e. tmUser, userId. tmUser needs to be handled by Customer Domain API. Moved out of claims
     */
    setUserState: createAction('[PanAppState - Platform] Set UserState for Session', props()),
    //action for successful session auth
    /**
     * @deprecated currently handles multiple unrelated state entities. Needs to be replace with start session to only handle token/session state
     */
    newSession: createAction('[Platform] New Session', props()),
};

const PAN_APP_STATE_LIB_CONFIG = new InjectionToken('PAN_APP_STATE_LIB_CONFIG');

const APP_STATE_KEY = 'app-state';
const STATE_VERSION_KEY = 'pan-state-version';

const FEATURE_FLAG_ACTIONS = {
    initialize: createAction('[PanAppState - Feature Flag] - Initialize'),
    initializeSuccess: createAction('[PanAppState - Feature Flag] - Initialize Success', props()),
    initializeFail: createAction('[PanAppState - Feature Flag] - Initialize Fail', props()),
    getSplitTreatments: createAction('[PanAppState - Feature Flag] - Get Split Names'),
    getSplitTreatmentsSuccess: createAction('[PanAppState - Feature Flag] - Get Split Treatments Success', props()),
    getSplitTreatmentsFail: createAction('[PanAppState - Feature Flag] - Get Split Treatments Fail', props())
};

const SESSION_ACTIONS = {
    //new session actions
    startSession: createAction('[Session] Start Session', props()),
    refreshSession: createAction('[Session] Refresh Session', props()),
    endSession: createAction('[Session] End Session', props()),
};

const MINIMUM_VERSIONS_ACTIONS = {
    getMinimumVersions: createAction('[PanAppState - Version] Gets Minimum Supported Versions'),
    getMinimumVersionsFail: createAction('[PanAppState - Version] Get Minimum Supported Versions Failed', props()),
    setMinimumVersions: createAction('[PanAppState - Version] Sets Minimum Supported Versions', props()),
};

class PageInfoService {
    constructor(router, store, route) {
        this.router = router;
        this.store = store;
        this.route = route;
        this.requestUrl = '';
        this.currentUrl = '';
    }
    /**
     * @remarks
     * This method handle Back Navigation for Touch (Mobile, Tablet)
     */
    getUrlPath() {
        return window.location.pathname;
    }
    getUrlQueryParamter(key) {
        const urlSearchParams = new URLSearchParams(window.location.search);
        const params = urlSearchParams.get(key);
        return params;
    }
    setCurrentUrl(currentUrl) {
        this.currentUrl = currentUrl;
        this.store.dispatch(PAGE_ACTIONS.setCurrentUrl({ currentUrl }));
    }
    setPreviousUrl(previousUrl) {
        this.store.dispatch(PAGE_ACTIONS.setPreviousUrl({ previousUrl }));
    }
    setCurrentAndPreviousUrls() {
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationStart && event.url !== '/') {
                this.requestUrl = event.url;
            }
            if (event instanceof NavigationEnd) {
                const url = decodeURIComponent(event.url);
                this.setPreviousUrl(this.currentUrl);
                this.setCurrentUrl(url);
            }
        });
    }
    goBack(url) {
        const qParamsRegEx = new RegExp(/[?&]+([^=&]+)=([^&]*)/gi);
        const hasQueryParamsRegex = new RegExp(/.*\?((.*=.*)(&?))+/g);
        let urlBuilder = url;
        const queryParamsBuilder = {};
        if (url.match(hasQueryParamsRegex)) {
            url.replace(qParamsRegEx, (m, key, value) => {
                queryParamsBuilder[key] = value;
            });
            urlBuilder = urlBuilder.split('?')[0];
        }
        this.router.navigate([urlBuilder], {
            queryParams: queryParamsBuilder,
            relativeTo: this.route,
        });
    }
    getQueryParams(incomingUrl) {
        const params = {};
        new URL(incomingUrl).searchParams.forEach((val, key) => {
            params[key] = val;
        });
        return params;
    }
    getUrlPathFromLink(incomingUrl) {
        return new URL(incomingUrl).pathname;
    }
    getHostname(incomingUrl) {
        return new URL(incomingUrl).hostname;
    }
    getUrlProtocol(incomingUrl) {
        return new URL(incomingUrl).protocol;
    }
    getUrlInfo() {
        const url = window.location.href;
        const info = {
            protocol: this.getUrlProtocol(url),
            hostName: this.getHostname(url),
            pathName: this.getUrlPathFromLink(url),
            queryParams: this.getQueryParams(url),
        };
        return info;
    }
    saveQueryParamsToStorage(key, params, url) {
        const queryParams = Object.assign({}, params);
        queryParams.returnUrl = url ?? '/';
        try {
            localStorage.setItem(key, JSON.stringify(queryParams));
        }
        catch (err) {
            console.error(err);
        }
    }
}
PageInfoService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PageInfoService, deps: [{ token: i1.Router }, { token: i2.Store }, { token: i1.ActivatedRoute }], target: i0.ɵɵFactoryTarget.Injectable });
PageInfoService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PageInfoService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PageInfoService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Router }, { type: i2.Store }, { type: i1.ActivatedRoute }]; } });

var PlatformEnum;
(function (PlatformEnum) {
    PlatformEnum["mobile"] = "mobile";
    PlatformEnum["tablet"] = "tablet";
    PlatformEnum["desktop"] = "desktop";
})(PlatformEnum || (PlatformEnum = {}));

class PlatformService {
    constructor(platform, store) {
        this.platform = platform;
        this.store = store;
        this.platformEnum = PlatformEnum;
        this.isTouch = new BehaviorSubject(false);
        this._isTouchExperience = this.isTouch
            .asObservable()
            .pipe(distinctUntilChanged());
    }
    get isTouchExperience$() {
        return this._isTouchExperience;
    }
    async setPlatFormType() {
        const deviceInfo = await Device.getInfo();
        this.platformType = this.getPlatformType();
    }
    getPlatformType() {
        const platformIsDesktop = this.platformIsDesktopAndNotIosOrAndroid();
        if (platformIsDesktop || !this.isTouch.value) {
            return this.platformEnum.desktop;
        }
        else if (this.platform.is('tablet')) {
            return this.platformEnum.tablet;
        }
        else {
            return this.platformEnum.mobile;
        }
    }
    platformIsDesktopAndNotIosOrAndroid() {
        return (this.platform.is('desktop') &&
            !(this.platform.is('ios') || this.platform.is('android')));
    }
    isTouchEnabled(event) {
        const wind = event?.window || {};
        return 'ontouchstart' in wind || navigator.maxTouchPoints > 0;
    }
    isWindows() {
        return navigator.appVersion.indexOf('Win') !== -1;
    }
    isChromium() {
        const chromiumOs = 'CrOS';
        return navigator.userAgent.includes(chromiumOs);
    }
    onResize(event) {
        const isTouch = !this.isWindows() && this.isTouchEnabled(event) && !this.isChromium();
        this.isTouch.next(isTouch);
        this.setPlatFormType();
        return isTouch;
    }
    getBrowserDimensions() {
        return {
            height: this.platform.height(),
            width: this.platform.width(),
            isLandscape: this.platform.isLandscape(),
            isPortrait: this.platform.isPortrait(),
        };
    }
    async getPlatformAndDeviceInfo() {
        const deviceInfo = await Device.getInfo();
        const platform = {
            height: this.platform.height(),
            width: this.platform.width(),
            isLandscape: this.platform.isLandscape(),
            isPortrait: this.platform.isPortrait(),
            platformTypes: this.platform.platforms(),
            platform: this.platform.is('desktop') ? 'DESKTOP' : 'MOBILE',
            context: deviceInfo.platform,
            operatingSystem: deviceInfo.operatingSystem,
            osVersion: deviceInfo.osVersion,
            model: deviceInfo.model,
            browserManufacturer: deviceInfo.manufacturer,
            browserVersion: deviceInfo.webViewVersion,
            platformType: this.getPlatformType(),
        };
        return platform;
    }
    async getNetworkStatus() {
        const status = await Network.getStatus();
        console.log('[Network] getting network status in platform service', status);
        return status?.connected;
    }
    trackScreenResizing() {
        this.platform.resize.subscribe(async () => {
            this.store.dispatch(PLATFORM_ACTIONS.setBrowserDimensions({
                dimensions: this.getBrowserDimensions(),
            }));
        });
    }
    async setNetworkStatus() {
        const isConnected = await this.getNetworkStatus();
        console.log('[Network] setting isConnected status from platform: ', isConnected);
        this.store.dispatch(NETWORK_ACTIONS.setStatus({ isConnected }));
    }
    monitorNetworkStatusChanges() {
        Network.addListener('networkStatusChange', (status) => {
            console.log('[Network] status change detected', status);
            this.setNetworkStatus();
        });
    }
}
PlatformService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PlatformService, deps: [{ token: i1$1.Platform }, { token: i2.Store }], target: i0.ɵɵFactoryTarget.Injectable });
PlatformService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PlatformService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PlatformService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$1.Platform }, { type: i2.Store }]; } });

const LOCAL_STORAGE_KEYS = {
    token: 'ecomr4-token',
    params: 'ecomr4-params',
    impersonator: 'ecomr4-impersonator',
    kmsi: 'ecomr4-kmsi',
    appIdle: 'app-idle',
    pushDeviceInfo: 'ecomr4-push-device-info',
    cacheCleared: 'cache-cleared',
    lastTappedPushWhileLoggedOff: 'lastTappedPushWhileLoggedOff',
    paymentCustomer: 'paymentCustomer',
    traceContext: 'trace-context',
    promoAppBanner: 'promo-app-banner',
    liveUpdateAvailable: 'live-update-available',
    shouldBlockReload: 'should-block-reload',
    displayLanguage: 'displayLanguage',
};
const SESSION_STORAGE_KEYS = {
    paymentsOutageMessageDismissed_LandingPage: 'payments-outage-message-dismissed-landing-page',
    paymentsOutageMessageDismissed_AllInvoicePage: 'payments-outage-message-dismissed-allInvoice-page',
    paymentOutageMessageDismissed_MakePaymentPage: 'payments-outage-message-dismissed-makePayment-page',
    paymentsInvoiceFilters: 'payments-invoice-filters',
};
var PREFERENCES_KEYS;
(function (PREFERENCES_KEYS) {
    PREFERENCES_KEYS["ACCESS_TOKEN"] = "access-token";
    PREFERENCES_KEYS["REFRESH_TOKEN"] = "refresh-token";
    PREFERENCES_KEYS["AUTH_CONTEXT"] = "auth-context";
    PREFERENCES_KEYS["AUTH_OPTIONS"] = "auth-options";
    PREFERENCES_KEYS["AUTH_RESPONSE"] = "auth-response";
    PREFERENCES_KEYS["PUNCHOUT_SESSION"] = "punchout-session";
    PREFERENCES_KEYS["PUNCHTHRU_SESSION"] = "punchthru-session";
    PREFERENCES_KEYS["MOXE_GUEST_UUID"] = "moxeGuestUUID";
    PREFERENCES_KEYS["GUEST_ZIP"] = "guest-zip";
})(PREFERENCES_KEYS || (PREFERENCES_KEYS = {}));
const AgentAttributes = {
    userName: 'userName',
    userId: 'userId',
    customerNumber: 'customerNumber',
    division: 'division',
    department: 'department',
    clientBuildNumber: 'clientBuildNumber',
};

class UsfTokenStorageService {
    constructor() { }
    getTokens() {
        return forkJoin([
            this.get(PREFERENCES_KEYS.ACCESS_TOKEN),
            this.get(PREFERENCES_KEYS.REFRESH_TOKEN),
        ]).pipe(take(1), map(([accessToken, refreshToken]) => {
            return {
                accessToken: accessToken,
                refreshToken: refreshToken,
            };
        }));
    }
    getAccessToken() {
        return window.sessionStorage.getItem(PREFERENCES_KEYS.ACCESS_TOKEN);
    }
    getContext() {
        return this.get(PREFERENCES_KEYS.AUTH_CONTEXT).pipe(map((data) => {
            return JSON.parse(data);
        }));
    }
    getOptions() {
        return from(Preferences.get({ key: PREFERENCES_KEYS.AUTH_OPTIONS }).then((options) => {
            if (!options?.value) {
                return null;
            }
            return JSON.parse(options.value);
        }));
    }
    // TEMPORARY
    getResponse() {
        return from(Preferences.get({ key: PREFERENCES_KEYS.AUTH_RESPONSE }).then((response) => {
            if (!response?.value) {
                return null;
            }
            return JSON.parse(response.value);
        }));
    }
    getPunchoutSession() {
        return from(Preferences.get({ key: PREFERENCES_KEYS.PUNCHOUT_SESSION }).then((punchoutSession) => {
            if (!punchoutSession?.value) {
                return null;
            }
            return JSON.parse(punchoutSession.value);
        }));
    }
    getPunchthruSession() {
        return from(Preferences.get({ key: PREFERENCES_KEYS.PUNCHTHRU_SESSION }).then((punchthruSession) => {
            if (!punchthruSession?.value) {
                return null;
            }
            return JSON.parse(punchthruSession.value);
        }));
    }
    saveTokens(tokens, options) {
        window.sessionStorage.setItem(PREFERENCES_KEYS.ACCESS_TOKEN, tokens.accessToken);
        window.sessionStorage.setItem(PREFERENCES_KEYS.REFRESH_TOKEN, tokens.refreshToken);
        if (options?.kmsi == true) {
            console.log('[UsfTokenStorageService] Persisting refresh token to storage for kmsi');
            Preferences.set({
                key: PREFERENCES_KEYS.REFRESH_TOKEN,
                value: tokens.refreshToken,
            });
        }
        return of();
    }
    // NOTE: saving auth context from the response instead of the request
    // due to auth api logic for determining a default customer context on initial login
    saveContext(context) {
        window.sessionStorage.setItem(PREFERENCES_KEYS.AUTH_CONTEXT, JSON.stringify(context));
        return from(Preferences.set({
            key: PREFERENCES_KEYS.AUTH_CONTEXT,
            value: JSON.stringify(context),
        }));
    }
    saveOptions(options) {
        const filteredOptions = {
            grantType: options.grantType,
            impersonationUserId: options.impersonationUserId,
            platform: options.platform,
            scopes: options.scopes,
            traceContext: options.traceContext,
            kmsi: options.kmsi,
            zipCode: options.zipCode,
        };
        window.sessionStorage.setItem(PREFERENCES_KEYS.AUTH_OPTIONS, JSON.stringify(options));
        return from(Preferences.set({
            key: PREFERENCES_KEYS.AUTH_OPTIONS,
            value: JSON.stringify(filteredOptions),
        }));
    }
    // TEMPORARY - used to allow setting the UserState after auth.  this should be decoupled from auth and trigger from a subsequent action.
    saveResponse(response) {
        console.log('[UsfTokenStorageService]: Saving response' + '\n', response);
        return from(Preferences.set({
            key: PREFERENCES_KEYS.AUTH_RESPONSE,
            value: JSON.stringify(response),
        }));
    }
    savePunchoutSession(punchoutSession) {
        console.log('[UsfTokenStorageService] Saving punchout session', punchoutSession);
        window.sessionStorage.setItem(PREFERENCES_KEYS.PUNCHOUT_SESSION, JSON.stringify(punchoutSession));
        return from(Preferences.set({
            key: PREFERENCES_KEYS.PUNCHOUT_SESSION,
            value: JSON.stringify(punchoutSession),
        }));
    }
    savePunchthruSession(punchthruSession) {
        console.log('[UsfTokenStorageService] Saving punchthru session', punchthruSession);
        window.sessionStorage.setItem(PREFERENCES_KEYS.PUNCHTHRU_SESSION, JSON.stringify(punchthruSession));
        return from(Preferences.set({
            key: PREFERENCES_KEYS.PUNCHTHRU_SESSION,
            value: JSON.stringify(punchthruSession),
        }));
    }
    saveMoxeGuestUUID(moxeGuestUUID) {
        console.log('[UsfTokenStorageService] Saving guest session', moxeGuestUUID);
        localStorage.setItem(PREFERENCES_KEYS.MOXE_GUEST_UUID, moxeGuestUUID);
        return from(Preferences.set({
            key: PREFERENCES_KEYS.MOXE_GUEST_UUID,
            value: moxeGuestUUID,
        }));
    }
    getMoxeGuestUUID() {
        if (localStorage.getItem(PREFERENCES_KEYS.MOXE_GUEST_UUID)) {
            return of(localStorage.getItem(PREFERENCES_KEYS.MOXE_GUEST_UUID));
        }
        else {
            return from(Preferences.get({ key: PREFERENCES_KEYS.MOXE_GUEST_UUID }).then((result) => {
                return result?.value || '';
            }));
        }
    }
    saveGuestZip(zip) {
        const zipValue = zip !== null ? zip.toString() : null;
        localStorage.setItem(PREFERENCES_KEYS.GUEST_ZIP, zipValue);
        console.log('[UsfTokenStorageService] Saving guest zip', zip);
        return from(Preferences.set({
            key: PREFERENCES_KEYS.GUEST_ZIP,
            value: zipValue,
        }));
    }
    getGuestZip() {
        const savedZip = localStorage.getItem(PREFERENCES_KEYS.GUEST_ZIP);
        if (savedZip) {
            return of(savedZip);
        }
        else {
            return from(Preferences.get({ key: PREFERENCES_KEYS.GUEST_ZIP }).then((result) => {
                return result?.value || null;
            }));
        }
    }
    //does a basic read-through pattern, preferring data from session storage and falling back to local storage
    get(key) {
        let data = window.sessionStorage.getItem(key);
        if (data) {
            return of(data);
        }
        else {
            return from(Preferences.get({ key: key }).then((data) => {
                return data?.value;
            }, (e) => {
                console.error('[UsfTokenStorageService] error getting Preferences', e);
                return undefined;
            }));
        }
    }
    clear() {
        window.sessionStorage.removeItem(PREFERENCES_KEYS.ACCESS_TOKEN);
        window.sessionStorage.removeItem(PREFERENCES_KEYS.REFRESH_TOKEN);
        return from(Preferences.clear());
    }
}
UsfTokenStorageService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: UsfTokenStorageService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
UsfTokenStorageService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: UsfTokenStorageService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: UsfTokenStorageService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return []; } });

class PanSplitService {
    constructor(splitService, config, store, tokenStorageService) {
        this.splitService = splitService;
        this.config = config;
        this.store = store;
        this.tokenStorageService = tokenStorageService;
        this.clientsInitialized = false;
        this.clientsUpdated = new Subject();
        this.clientsInitializedSubject = new BehaviorSubject(false);
        this.clientsInitialized$ = this.clientsInitializedSubject.asObservable();
        this.splitUpdateRefreshTime = 300;
        this.evaluateFeature = (feature) => {
            return this.treatmentEnabled(this.splitService.getTreatment(feature), feature);
        };
        this.evaluateFeatureWithTraffictype = (feature) => {
            const split = this.splitService.getSplit(feature);
            return this.treatmentEnabled(this.getTreatmentByTrafficType(split), feature);
        };
        this.treatmentEnabled = (treatment, feature) => {
            if (treatment === 'control') {
                const controlConfig = this.config.featureFlagSettings?.features[feature]
                    ?.control;
                const result = controlConfig ?? false;
                if (controlConfig === undefined) {
                    console.warn('Flag - treatmentEnabled (control) - flag was not found: ', feature, result);
                }
                return result;
            }
            else {
                return treatment === 'on';
            }
        };
        if (config.featureFlagSettings) {
            this.initializeSplit();
        }
    }
    initializeSplit() {
        //create initial config(same as before)
        const sdkConfig = {
            core: {
                authorizationKey: this.config.featureFlagSettings.authorizationKey,
                key: 'UNKNOWN', //placeholder
            },
            scheduler: {
                featuresRefreshRate: this.splitUpdateRefreshTime,
                segmentsRefreshRate: this.splitUpdateRefreshTime,
            },
            streamingEnabled: false,
        };
        this.populateControlState();
        this.splitService.init(sdkConfig);
        this.splitService.sdkReady$.pipe(take(1)).subscribe(() => {
            this.persistTreatments();
        });
    }
    updateSplitWithSession(user, customer, customization) {
        this.currentUserName = user.userId;
        if (user.userId === 'GUESTUSER') {
            // For guest users, check for existing guest UUID
            this.tokenStorageService
                .getMoxeGuestUUID()
                .pipe(take(1))
                .subscribe((result) => {
                const guestUUID = result;
                if (!!guestUUID) {
                    // If existing guest UUID: set it as the userKey and save to preferences
                    this.userKey = guestUUID;
                    this.tokenStorageService.saveMoxeGuestUUID(guestUUID);
                }
                else {
                    // If no existing guest UUID: generate a new UUID, set it as the userKey and save to preferences
                    const newUUID = crypto.randomUUID();
                    this.userKey = newUUID;
                    this.tokenStorageService.saveMoxeGuestUUID(newUUID);
                }
                this.initializeClients(customer, customization);
            });
        }
        else {
            // For non-guest users, use the userId as the userKey
            this.userKey = user.userId;
            this.initializeClients(customer, customization);
        }
    }
    initializeClients(customer, customization) {
        this.customerKey = customer.customerNumber.toString();
        this.divisionKey = customer.divisionNumber.toString();
        this.customizationKey = customization?.toUpperCase();
        let clientObservables = [];
        if (this.splitService.getSDKClient(this.userKey) === undefined) {
            const obs = this.splitService.initClient(this.userKey);
            clientObservables.push(obs);
        }
        if (this.splitService.getSDKClient(this.customerKey) === undefined) {
            const obs = this.splitService.initClient(this.customerKey);
            clientObservables.push(obs);
        }
        if (this.splitService.getSDKClient(this.divisionKey) === undefined) {
            const obs = this.splitService.initClient(this.divisionKey);
            clientObservables.push(obs);
        }
        if (!!this.customizationKey &&
            this.splitService.getSDKClient(this.customizationKey) === undefined) {
            const obs = this.splitService.initClient(this.customizationKey);
            clientObservables.push(obs);
        }
        combineLatest(clientObservables)
            .pipe(take(1))
            .subscribe(() => {
            this.clientsInitialized = true;
            this.clientsInitializedSubject.next(true);
            this.clientsUpdated.next();
            if (this.splitService.isSDKReady) {
                //If not ready yet, persisting will be covered by the constructor
                this.persistTreatments();
            }
        });
    }
    //returns observable of sdkUpdate combined with call for treatment
    watchFeaturesEnabled(features, allEnabled = true) {
        return merge(this.splitService.sdkUpdate$, this.clientsUpdated).pipe(startWith('Initial Values'), mergeMap(() => {
            return this.featuresEnabled(features, allEnabled);
        }), distinctUntilChanged());
    }
    featuresEnabled(features, allEnabled = true) {
        if (!this.clientsInitialized) {
            const result = allEnabled
                ? features.every(this.evaluateFeature)
                : features.some(this.evaluateFeature);
            return of(result);
        }
        const result = allEnabled
            ? features.every(this.evaluateFeatureWithTraffictype)
            : features.some(this.evaluateFeatureWithTraffictype);
        return of(result);
    }
    featuresEnabledWithTimeout(features, timeoutMs, allEnabled = true) {
        return merge(this.clientsInitialized$.pipe(filter((initialized) => initialized), take(1)), timer(timeoutMs).pipe(take(1))).pipe(first(), mergeMap(() => this.watchFeaturesEnabled(features, allEnabled)));
    }
    async featureEnabledAsPromise(feature) {
        if (this.clientsInitialized) {
            return this.evaluateFeatureWithTraffictype(feature);
        }
        else {
            return this.evaluateFeature(feature);
        }
    }
    //called from within subscription
    persistTreatments() {
        let featureMap = {};
        if (!this.clientsInitialized) {
            //get from default client
            const splits = this.splitService.getSplitNames();
            const treatments = this.splitService.getTreatments(splits);
            splits.forEach((split) => {
                featureMap[split] = this.treatmentEnabled(treatments[split], split);
            });
        }
        else {
            const splits = this.splitService.getSplits();
            splits.forEach((split) => {
                const treatment = this.getTreatmentByTrafficType(split);
                featureMap[split.name] = this.treatmentEnabled(treatment, split.name);
            });
        }
        this.store.dispatch(FEATURE_FLAG_ACTIONS.getSplitTreatmentsSuccess({ splits: featureMap }));
    }
    populateControlState() {
        const splits = {};
        for (const value of Object.values(this.config.featureFlagSettings?.features)) {
            splits[value.name] = value.control;
        }
        this.store.dispatch(FEATURE_FLAG_ACTIONS.getSplitTreatmentsSuccess({
            splits: splits,
        }));
    }
    getTreatmentByTrafficType(split) {
        let key;
        switch (split?.trafficType) {
            case 'user':
                key = this.userKey;
                break;
            case 'customer':
                key = this.customerKey;
                break;
            case 'division':
                key = this.divisionKey;
                break;
            case 'customization':
                key = this.customizationKey;
                break;
            case 'dynamic':
                key = this.userKey;
                break;
            default:
                key = this.userKey;
        }
        const attributes = {
            customer: this.customerKey,
            customization: this.customizationKey,
            division: this.divisionKey,
            user: this.userKey,
            experience: this.currentUserName === 'GUESTUSER' ? 'guest' : '',
        };
        return this.splitService.getTreatment(key, split?.name, attributes);
    }
}
PanSplitService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PanSplitService, deps: [{ token: i1$2.SplitService }, { token: PAN_APP_STATE_LIB_CONFIG }, { token: i2.Store }, { token: UsfTokenStorageService }], target: i0.ɵɵFactoryTarget.Injectable });
PanSplitService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PanSplitService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PanSplitService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$2.SplitService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [PAN_APP_STATE_LIB_CONFIG]
                }] }, { type: i2.Store }, { type: UsfTokenStorageService }]; } });

class PanAppState {
    constructor(store, config, injector, browserStorage, pageInfoService, platformService, splitService) {
        this.store = store;
        this.config = config;
        this.injector = injector;
        this.browserStorage = browserStorage;
        this.pageInfoService = pageInfoService;
        this.platformService = platformService;
        this.splitService = splitService;
        this.accessToken$ = this.store
            .select(selectAccessToken())
            .pipe(filter((token) => !!token));
        this.refreshToken$ = this.store.select(selectRefreshToken());
        this.tokenExpiration$ = this.store.select(selectTokenExpiration());
        this.token$ = this.store.select(selectToken());
        this.customer$ = this.store.select(selectCustomer());
        this.user$ = this.store.select(selectLoggedInUser());
        this.userProfiles$ = this.store.select(selectUserProfiles());
        this.online$ = this.store.select(selectOnline());
        this.pageTitle$ = this.store.select(selectPageTitle());
        this.currentUrl$ = this.store.select(selectCurrentUrl());
        this.previousUrl$ = this.store.select(selectPreviousUrl());
        this.originType$ = this.store.select(selectOriginType());
        this.linkSource$ = this.store.select(selectLinkSource());
        this.rank$ = this.store.select(selectRank());
        this.platform$ = this.store.select(selectPlatform());
        this.platformType$ = this.store.select(selectPlatformType());
        this.logrocketUrl$ = this.store.select(selectLogrocketUrl());
        /**
         * session$
         *
         * @remarks
         * this includes data from authorization,
         * and fires once on new authorizations
         */
        this.session$ = this.store.select(selectSession()).pipe(filter((session) => !!session), distinctUntilChanged((previousSession, session) => {
            return (previousSession.ecomUserId == session.ecomUserId &&
                previousSession.selectedCustomerId == session.selectedCustomerId &&
                previousSession.department == session.department &&
                previousSession.division == session.division &&
                previousSession.startTime == session.startTime);
        }));
        /**
         * userReady$
         *
         * @remarks
         * Emits once when a valid user becomes available in state.
         * @returns
         * UserState of the logged-in user.
         */
        this.userReady$ = this.user$.pipe(filter((user) => !!user?.userId && !!user?.ecomUserId), distinctUntilChanged((prev, curr) => prev?.userId === curr?.userId), map((user) => user));
        /**
         * userLogout$
         *
         * @remarks
         * Emits once when the user logs out.
         * @returns
         * The UserState of the user who just logged out.
         */
        this.userLogout$ = this.user$.pipe(startWith(null), pairwise(), filter(([prev, curr]) => prev !== null && curr === null), map(([prev]) => prev));
        this.stateVersion = new BehaviorSubject(this.version);
        this.customization$ = new BehaviorSubject(undefined);
        this.feature$ = (features, allEnabled = true) => {
            /**
             * @param features - array of features to evaluate on/off status
             * @param allEnabled - true = all items in array must be enabled.  false = any item in list is enabled
             * @returns observable boolean value of true/false that represents if a feature is on/off
             */
            return this.splitService.watchFeaturesEnabled(features, allEnabled);
        };
        this.featureWithTimeout$ = (features, timeoutMs, allEnabled = true) => {
            /**
             * @param features - array of features to evaluate on/off status
             * @param timeoutMs - number of milliseconds to wait for the features to be initialized until we use the control value
             * @param allEnabled - true = all items in array must be enabled.  false = any item in list is enabled
             * @returns observable boolean value of true/false that represents if a feature is on/off
             */
            return this.splitService.featuresEnabledWithTimeout(features, timeoutMs, allEnabled);
        };
        this.monitorStateVersion();
        this.setNetworkStatus();
        this.platformService.monitorNetworkStatusChanges();
        this.pageInfoService.setCurrentAndPreviousUrls();
        this.platformService.trackScreenResizing();
        this.platformService.setPlatFormType();
        this.platformService.getPlatformAndDeviceInfo().then((info) => {
            this.setPlatform(info);
        });
    }
    /**
     * monitorStateVersion
     *
     * @remarks
     * This will monitor the state version in order
     * to determine if it is necessary to wipe the indexedDB
     * and re-fetch the data from a server
     */
    monitorStateVersion() {
        this.stateVersion
            .pipe(filter((version) => version < this.config.stateVersion), concatMap((_) => this.wipeStorage()))
            .subscribe((done) => {
            if (done) {
                console.warn('state sync completed.');
            }
        });
    }
    get logEnv() {
        return this.config.openTelemetrySettings?.environment;
    }
    refreshState() {
        window.location.reload();
        if (this.logEnv !== 'prod') {
            console.warn('state sync refresh');
        }
    }
    wipeStorage() {
        const worker$ = new Observable((observer) => {
            // call browser storage service
            this.browserStorage
                .deleteAllDbs()
                .pipe(take(1))
                .subscribe(({ done, startsEmpty }) => {
                if (done) {
                    this.version = this.config.stateVersion;
                    // prevent client unit tests from failing due to refreshing window
                    if (!startsEmpty) {
                        this.refreshState();
                    }
                }
                observer.next(done);
            });
        });
        return worker$;
    }
    set version(value) {
        localStorage.setItem(STATE_VERSION_KEY, value.toString());
        this.stateVersion.next(value);
    }
    get version() {
        return Number(localStorage.getItem(STATE_VERSION_KEY)) || 0;
    }
    /**
     *@deprecated
     *Use persistTokenState instread
     */
    persistToken(loginResponse) {
        console.log('Persisting token to state');
        this.store.dispatch(TOKEN_ACTIONS.loadTokenSuccess({
            loginResponse,
        }));
    }
    persistTokenState(tokenState) {
        this.store.dispatch(TOKEN_ACTIONS.saveToken({ token: tokenState }));
    }
    startSession(sessionState) {
        this.store.dispatch(SESSION_ACTIONS.startSession({ session: sessionState }));
    }
    clearTokenState() {
        this.store.dispatch(TOKEN_ACTIONS.clearTokenState());
    }
    persistCustomer(selectedCustomer) {
        this.store.dispatch(CUSTOMER_ACTIONS.loadSelectedCustomerSuccess({
            selectedCustomer,
        }));
    }
    persistUser(loggedInUser) {
        this.store.dispatch(USER_ACTIONS.loadUserSuccess({
            loggedInUser,
        }));
    }
    persistAssignedCustomers(assignedCustomers) {
        this.store.dispatch(USER_ACTIONS.loadAssignedCustomers({
            assignedCustomers,
        }));
    }
    getDistinctSelectedCustomer() {
        return this.customer$.pipe(filter((customer) => !!customer?.hasOwnProperty('customerName') &&
            !!customer?.hasOwnProperty('customerNumber') &&
            !!customer?.hasOwnProperty('departmentNumber')), distinctUntilChanged((curCustomer, prevCustomer) => prevCustomer.customerName === curCustomer.customerName &&
            prevCustomer.customerNumber === curCustomer.customerNumber &&
            prevCustomer.departmentNumber === curCustomer.departmentNumber));
    }
    setOnline() {
        this.store.dispatch(NETWORK_ACTIONS.setOnline());
    }
    setOffline() {
        this.store.dispatch(NETWORK_ACTIONS.setOffline());
    }
    async setNetworkStatus() {
        this.platformService.setNetworkStatus();
    }
    setPageTitle(pageTitle) {
        this.store.dispatch(PAGE_ACTIONS.setPageTitle({ pageTitle }));
    }
    setPlatform(platform) {
        this.store.dispatch(PLATFORM_ACTIONS.setPlatform({ platform }));
    }
    setOriginType(originType) {
        this.store.dispatch(PAGE_ACTIONS.setOriginType({ originType }));
    }
    setLinkSource(linkSource) {
        this.store.dispatch(PAGE_ACTIONS.setLinkSource({ linkSource }));
    }
    setRank(rank) {
        this.store.dispatch(PAGE_ACTIONS.setRank({ rank }));
    }
    clearStore() {
        this.store.dispatch(TOKEN_ACTIONS.clearStore());
    }
    getLogRocketUrl() {
        LogRocket.getSessionURL((url) => {
            this.store.dispatch(PAGE_ACTIONS.setLogrocketUrl({ url }));
        });
    }
    getMinimumVersions() {
        this.store.dispatch(MINIMUM_VERSIONS_ACTIONS.getMinimumVersions());
    }
    getCustomization$() {
        return this.customization$?.pipe(filter((customization) => customization !== undefined));
    }
    setCustomization(customization) {
        if (customization !== undefined) {
            this.customization$?.next(customization);
        }
    }
    // this method needs to be updated to use session state so it can be used in the client as well
    /**
     * buildFeedbackVariables
     *
     * @remarks
     * this is used by the client to configure customer variables the native Usabilla SDK
     *
     * @param user
     * @param customer
     * @param currentUrl
     * @param logrocketUrl
     * @returns
     * UsabillaCustomVariables object
     */
    buildFeedbackVariables(user, customer, currentUrl, logrocketUrl) {
        const variables = {
            feedbackUrl: currentUrl || '',
            logrocketUrl: logrocketUrl?.toString() || '',
            userName: user.userId || '',
            userType: user.userKind || '',
            ecomUserId: user.ecomUserId?.toString() || '',
            buildNumber: this.config.buildNumber || '',
            divisionNumber: customer.divisionNumber?.toString() || '',
            customerNumber: customer.customerNumber?.toString() || '',
            departmentNumber: customer.departmentNumber?.toString() || '',
        };
        return variables;
    }
    /**
     * stateIsOutOfDate
     *
     * @remarks
     * Used to determine if the users offline storage needs to be wiped and refreshed
     *
     * @returns true/false
     */
    stateIsOutOfDate() {
        const incomingStateVersion = this.config.stateVersion;
        return Number(incomingStateVersion) > Number(this.version);
    }
    monitorNetworkStatusChanges() {
        Network.addListener('networkStatusChange', (status) => {
            console.log('[Network] getting network status in app-state-facade', status);
            this.setNetworkStatus();
        });
    }
    initializeFeatureFlags() {
        combineLatest([this.user$, this.customer$, this.customization$])
            .pipe(filter(([user, customer]) => !!user && !!customer), distinctUntilChanged(([previousUser, previousCustomer, previousCustomization], [currentUser, currentCustomer, currentCustomization]) => {
            return (previousUser.userId === currentUser.userId &&
                previousCustomer.customerNumber ===
                    currentCustomer.customerNumber &&
                previousCustomization === currentCustomization);
        }))
            .subscribe(([user, customer, customization]) => {
            this.splitService.updateSplitWithSession(user, customer, customization);
        });
    }
    persistFeatureFlagTreatments(splits) {
        this.store.dispatch(FEATURE_FLAG_ACTIONS.getSplitTreatmentsSuccess({ splits }));
    }
    async featureEnabled(feature) {
        return await this.splitService.featureEnabledAsPromise(feature);
    }
}
PanAppState.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PanAppState, deps: [{ token: i2.Store }, { token: PAN_APP_STATE_LIB_CONFIG }, { token: i0.Injector }, { token: i2$1.BrowserStorageService }, { token: PageInfoService }, { token: PlatformService }, { token: PanSplitService }], target: i0.ɵɵFactoryTarget.Injectable });
PanAppState.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PanAppState });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PanAppState, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i2.Store }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [PAN_APP_STATE_LIB_CONFIG]
                }] }, { type: i0.Injector }, { type: i2$1.BrowserStorageService }, { type: PageInfoService }, { type: PlatformService }, { type: PanSplitService }]; } });

const TRACE_PARENT_HEADER = 'traceparent';
class TracingService {
    constructor(platformLocation, config) {
        this.platformLocation = platformLocation;
        this.config = config;
        this.parentSpans = {};
    }
    initializeProviderAndContext(config) {
        const collectorOptions = {
            url: config.url,
            headers: { ...config.headers },
            concurrencyLimit: config.concurrencyLimit, // an optional limit on pending requests
        };
        const traceExporter = new CollectorTraceExporter(collectorOptions);
        const resource = new Resource({
            'service.name': config.appName,
            'deployment.environment': config.environment,
            'telemetry.sdk.language': 'javascript',
            'host.name': 'browser',
        });
        this.provider = new WebTracerProvider({ resource });
        this.provider.addSpanProcessor(new BatchSpanProcessor(traceExporter));
        // Changing default contextManager to use ZoneContextManager - supports asynchronous operations - optional
        this.contextManager = new ZoneContextManager();
        this.provider.register({
            propagator: new HttpTraceContextPropagator(),
            contextManager: this.contextManager,
        });
    }
    initHttpSpan(request, traceContext) {
        const urlRequest = request.urlWithParams.startsWith('http')
            ? new URL(request.urlWithParams)
            : new URL(this.getURL());
        if (this.parentSpans[traceContext]) {
            return this.initChildHttpSpan(request, this.parentSpans[traceContext]);
        }
    }
    /**
     *
     * Initialise a span for a request intercepted
     *
     * @param request request
     */
    initParentHttpSpan(request) {
        const urlRequest = request.urlWithParams.startsWith('http')
            ? new URL(request.urlWithParams)
            : new URL(this.getURL());
        const span = this.provider.getTracer('ngrx-span').startSpan(`${urlRequest.protocol
            .replace(':', '')
            .toUpperCase()} ${request.method.toUpperCase()} ${urlRequest.pathname}`, {
            attributes: {
                // TODO:  WILL NEED DUMMY VALUES WHEN HTTP REQUEST IS NOT THERE
                ['http.method']: request.method,
                ['http.url']: request.urlWithParams,
                ['http.host']: urlRequest.host,
                ['http.scheme']: urlRequest.protocol.replace(':', ''),
                ['http.target']: urlRequest.pathname + urlRequest.search,
                ['http.user_agent']: window.navigator.userAgent,
                // TODO:  SEE WHAT NEW RELIC WILL BE ABLE TO INDEX (USER/CUSTOMER/ORDERID/ETC)
            },
            kind: api.SpanKind.CLIENT,
        }, context.active());
        return span;
    }
    initChildHttpSpan(request, parent) {
        // Start another span. In this example, the main method already started a
        // span, so that'll be the parent span, and this will be a child span.
        const isActiveParent = parent?.isRecording() || '';
        if (!isActiveParent) {
            return this.initParentHttpSpan(request);
        }
        else {
            const urlRequest = request.urlWithParams.startsWith('http')
                ? new URL(request.urlWithParams)
                : new URL(this.getURL());
            const ctx = trace.setSpan(this.contextManager.active(), parent);
            const span = this.provider.getTracer('ngrx-span').startSpan(`${urlRequest.protocol
                .replace(':', '')
                .toUpperCase()} ${request.method.toUpperCase()} ${urlRequest.pathname}`, {
                attributes: {
                    // TODO:  WILL NEED DUMMY VALUES WHEN HTTP REQUEST IS NOT THERE
                    ['http.method']: request.method,
                    ['http.url']: request.urlWithParams,
                    ['http.host']: urlRequest.host,
                    ['http.scheme']: urlRequest.protocol.replace(':', ''),
                    ['http.target']: urlRequest.pathname + urlRequest.search,
                    ['http.user_agent']: window.navigator.userAgent,
                    // TODO:  SEE WHAT NEW RELIC WILL BE ABLE TO INDEX (USER/CUSTOMER/ORDERID/ETC)
                },
                kind: api.SpanKind.CLIENT,
            }, ctx);
            return span;
        }
    }
    initParentSpan(data) {
        const span = this.provider.getTracer('ngrx-span').startSpan(data.attributes.action, {
            attributes: data.attributes,
            kind: api.SpanKind.CLIENT,
        }, context.active());
        return span;
    }
    initChildSpan(parent, data) {
        const ctx = trace.setSpan(context.active(), parent);
        const span = this.provider.getTracer('ngrx-span').startSpan(data.attributes.action, {
            attributes: data.attributes,
            kind: api.SpanKind.CLIENT,
        }, ctx);
        return span;
    }
    /**
     * Get current scheme, hostname and port
     */
    getURL() {
        return this.platformLocation.href;
    }
    /**
     * Add header propagator in request and conserve original header
     *
     *  @param request request
     */
    injectContextAndHeader(request, span) {
        const carrier = {};
        const setter = api.defaultTextMapSetter;
        const spanContext = span.spanContext();
        const traceParent = `00-${spanContext.traceId}-${spanContext.spanId}-0${Number(spanContext.traceFlags || TraceFlags.NONE).toString(16)}`;
        setter.set(carrier, TRACE_PARENT_HEADER, traceParent);
        api.propagation.inject(this.contextManager.active(), carrier, setter);
        request.headers.keys().map((key) => {
            carrier[key] = request.headers.get(key);
        });
        return request.clone({
            setHeaders: carrier,
        });
    }
    /**
     * Set custom attributes in span with a CustomSpan
     *
     * @param span
     * @param request
     * @param response
     * @returns Span
     */
    setCustomSpan(span, request, response) {
        // return this.customSpan != null ? this.customSpan.add(span, request, response) : span;
        return span;
    }
    get traceEnv() {
        return this.config.openTelemetrySettings?.environment;
    }
}
TracingService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: TracingService, deps: [{ token: i1$3.PlatformLocation }, { token: PAN_APP_STATE_LIB_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
TracingService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: TracingService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: TracingService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$3.PlatformLocation }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [PAN_APP_STATE_LIB_CONFIG]
                }] }]; } });

class UsabillaService {
    constructor(config) {
        this.config = config;
        if (!Capacitor.isNativePlatform()) {
            this.injectUsabillaJS(this.config.usabillaSettings.scriptSrc, this.config.usabillaSettings.scriptId);
        }
    }
    setCustomVariables(data) {
        console.log('[Usabilla] Setting custom variables', data);
        if (window.hasOwnProperty('usabilla_live') && !this.isEmpty(data)) {
            window['usabilla_live']('data', {
                custom: {
                    ...data,
                },
            });
        }
    }
    customTrigger(campaignName, data = {}) {
        this.setCustomVariables(data);
        window['usabilla_live']('trigger', campaignName);
    }
    injectUsabillaJS(scriptSrc, scriptId, data) {
        const customData = data || '';
        if (!Capacitor.isNativePlatform() &&
            !window.hasOwnProperty('usabilla_live')) {
            const script = document.createElement('script');
            script.type = 'text/javascript';
            script.src = scriptSrc;
            script.id = scriptId;
            script.async = false;
            document.getElementsByTagName('head')[0].appendChild(script);
            if (customData) {
                this.setCustomVariables(customData);
            }
        }
        else {
            if (window.hasOwnProperty('usabilla_live') && customData) {
                this.setCustomVariables(customData);
            }
        }
    }
    // loads usabilla feedback script in ecom app to correctly position the button
    styleScript() {
        if (!Capacitor.isNativePlatform()) {
            if (window.hasOwnProperty('usabilla_live')) {
                const script = document.createElement('script');
                script.type = 'text/javascript';
                script.src = 'assets/js/usabilla_position.js';
                script.id = 'usabillaButtonPosition';
                script.async = false;
                document.getElementsByTagName('head')[0].appendChild(script);
            }
        }
    }
    loadCSS() {
        if (!Capacitor.isNativePlatform()) {
            if (window.hasOwnProperty('usabilla_live')) {
                const script = document.createElement('script');
                script.type = 'text/javascript';
                script.src = 'assets/js/ub_event_callback_screenshot.js';
                script.id = 'usabillaLoadCSS';
                script.async = false;
                document.getElementsByTagName('head')[0].appendChild(script);
            }
        }
    }
    show() {
        window['usabilla_live']('show');
        this.styleScript();
        this.loadCSS();
    }
    hide() {
        window['usabilla_live']('hide');
    }
    click() {
        window['usabilla_live']('click');
    }
    isEmpty(obj) {
        for (const x in obj) {
            if (obj.hasOwnProperty(x)) {
                return false;
            }
        }
        return true;
    }
    resetCustomSurvey(buttonId, campaignId) {
        localStorage.removeItem(`usbl.${buttonId}.c.${campaignId}`);
        this.virtualPageView();
    }
    virtualPageView() {
        window['usabilla_live']('virtualPageView');
    }
}
UsabillaService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: UsabillaService, deps: [{ token: PAN_APP_STATE_LIB_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
UsabillaService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: UsabillaService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: UsabillaService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [PAN_APP_STATE_LIB_CONFIG]
                }] }]; } });

class TrackingService {
    constructor(tracingService, usabilla) {
        this.tracingService = tracingService;
        this.usabilla = usabilla;
        this.appEventDataClearDate = undefined;
        window['appEventData'] = [];
        this.appEventDataClearDate = new Date().getDate();
    }
    sendWebAnalytics(data) {
        const currentDate = new Date().getDate();
        if (this.appEventDataClearDate != currentDate) {
            window['appEventData'] = [];
            this.appEventDataClearDate = currentDate;
        }
        // push to adobe
        window['appEventData'] = window['appEventData'] || [];
        window['appEventData'].push(data);
    }
    sendLogs(logData, spanAttributes) {
        this.tracingService.contextManager.disable();
        this.tracingService.contextManager.enable();
        const logSpanData = { ...logData, attributes: spanAttributes };
        const span = this.tracingService.initParentSpan(logSpanData);
        span.end();
    }
    sendTracing(tracing, spanAttributes) {
        // push to open telementry
        this.tracingService.contextManager.disable();
        this.tracingService.contextManager.enable();
        // get trace context if it exists
        const context = tracing.data.traceContext || '';
        if (!context) {
            return;
        }
        else {
            // check dictionary for tracing token
            const parentSpan = this.tracingService.parentSpans[context] || null;
            const traceData = { ...tracing, attributes: spanAttributes };
            if (traceData.data.isStartOfTrace) {
                // create a new parent span with the trace context
                localStorage.setItem(LOCAL_STORAGE_KEYS.traceContext, context);
                const parent = this.tracingService.initParentSpan(traceData);
                this.tracingService.parentSpans[tracing.data.traceContext] = parent;
            }
            else if (this.tracingService.parentSpans[context]?.isRecording()) {
                //if the parent is not recording, don't create a new orphaned span as this will create a dump trace as well
                // if parent span exists, create child span
                const childSpan = this.tracingService.initChildSpan(this.tracingService.parentSpans[context], traceData);
                childSpan.end();
            }
            if (tracing.data.isEndOfTrace &&
                this.tracingService.parentSpans[context]?.isRecording()) {
                this.tracingService.parentSpans[context].end();
                delete this.tracingService.parentSpans[context];
                localStorage.removeItem(LOCAL_STORAGE_KEYS.traceContext);
            }
        }
    }
    triggerCustomFeedback(campaignName, data) {
        // open get feedback with custom trigger
        this.usabilla.customTrigger(campaignName, data);
    }
    get trackEnv() {
        return this.tracingService.traceEnv;
    }
}
TrackingService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: TrackingService, deps: [{ token: TracingService }, { token: UsabillaService }], target: i0.ɵɵFactoryTarget.Injectable });
TrackingService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: TrackingService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: TrackingService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: TracingService }, { type: UsabillaService }]; } });

var TokenAuthMode;
(function (TokenAuthMode) {
    TokenAuthMode[TokenAuthMode["Automation"] = 0] = "Automation";
    TokenAuthMode[TokenAuthMode["B2C"] = 1] = "B2C";
    TokenAuthMode[TokenAuthMode["Local"] = 2] = "Local";
    TokenAuthMode[TokenAuthMode["Impersonation"] = 3] = "Impersonation";
    TokenAuthMode[TokenAuthMode["Invitation"] = 4] = "Invitation";
    TokenAuthMode[TokenAuthMode["Punchout"] = 5] = "Punchout";
    TokenAuthMode[TokenAuthMode["Punchthru"] = 6] = "Punchthru";
    TokenAuthMode[TokenAuthMode["NvPunchthru"] = 7] = "NvPunchthru";
    TokenAuthMode[TokenAuthMode["Refresh"] = 8] = "Refresh";
    TokenAuthMode[TokenAuthMode["Guest"] = 9] = "Guest";
})(TokenAuthMode || (TokenAuthMode = {}));

class UsfTokenClient {
    constructor(httpClient, config) {
        this.httpClient = httpClient;
        this.config = config;
    }
    get(mode, options) {
        let request;
        switch (mode) {
            case TokenAuthMode.B2C:
                request = this.buildB2CRequest(options);
                break;
            case TokenAuthMode.Automation:
                request = this.buildPasswordRequest(options);
                break;
            case TokenAuthMode.Refresh:
                request = this.buildRefreshRequest(options);
                break;
            case TokenAuthMode.Punchout:
                request = this.buildPunchoutRequest(options);
                break;
            case TokenAuthMode.Punchthru:
            case TokenAuthMode.NvPunchthru:
                request = this.buildPunchthruRequest(mode, options);
                break;
            case TokenAuthMode.Guest:
                request = this.buildGuestRequest(options);
                break;
        }
        const httpOptions = this.buildHttpOptionsForRequest(request, options);
        return this.httpClient
            .post(`${this.config.tokenServiceSettings.authApiTokenUrl}`, request, { ...httpOptions })
            .pipe(retryBackoff(defaultApiRetryStrategy));
    }
    removeAll(userId, userType, token) {
        const httpOptions = {
            headers: new HttpHeaders({
                'content-type': 'application/json',
                'consumer-id': 'ecomr4',
                'correlation-id': v4(),
                'trace-context': 'logout',
                idToken: 'Bearer ' + token,
            }),
            withCredentials: false,
        };
        const request = {
            userName: userId,
            userType: userType,
        };
        return this.httpClient.post(`${this.config.tokenServiceSettings.authApiLogoutUrl}`, request, { ...httpOptions });
    }
    buildB2CRequest(authOptions) {
        return {
            grantType: 'b2c',
            scopes: authOptions.scopes ?? UsfTokenClient.DefaultScopes,
            platform: authOptions.platform ?? '',
            authContext: authOptions.authContext ?? UsfTokenClient.DefaultContext,
            refreshToken: '',
            idToken: authOptions.idToken,
        };
    }
    buildPasswordRequest(authOptions) {
        return {
            grantType: 'password',
            scopes: authOptions.scopes ?? UsfTokenClient.DefaultScopes,
            platform: authOptions.platform ?? '',
            authContext: authOptions.authContext,
            refreshToken: '',
            idToken: authOptions.idToken,
        };
    }
    buildRefreshRequest(authOptions) {
        return {
            grantType: 'refreshToken',
            scopes: authOptions.scopes ?? UsfTokenClient.DefaultScopes,
            platform: authOptions.platform ?? '',
            authContext: authOptions.authContext,
            refreshToken: authOptions.refreshToken,
            idToken: authOptions.idToken,
        };
    }
    buildPunchoutRequest(authOptions) {
        return {
            grantType: 'punchout',
            scopes: authOptions.scopes ?? UsfTokenClient.DefaultScopes,
            platform: authOptions.platform ?? '',
            punchoutToken: authOptions.punchoutToken,
        };
    }
    buildPunchthruRequest(mode, authOptions) {
        let grantType = mode === TokenAuthMode.NvPunchthru ? 'nv-punchthru' : 'punchthru';
        return {
            grantType,
            scopes: authOptions.scopes ?? UsfTokenClient.DefaultScopes,
            platform: authOptions.platform ?? '',
            punchthruToken: authOptions.punchthruToken,
        };
    }
    buildGuestRequest(options) {
        return {
            grantType: 'guest',
            scopes: 'usf-user usf-customer usf-product',
            platform: 'DESKTOP',
            zipCode: options?.zipCode,
        };
    }
    buildHttpOptionsForRequest(request, options) {
        // TODO: research requirements and remove or refactor this
        let correlationIdPrefix;
        if (request.grantType == 'password') {
            correlationIdPrefix = options.username;
        }
        return {
            headers: new HttpHeaders({
                'content-type': 'application/json',
                'consumer-id': 'ecomr4',
                'correlation-id': correlationIdPrefix + v4(),
                'trace-context': 'login', //NOTE: look into other trace-context logic around the app
            }),
            withCredentials: false,
        };
    }
}
UsfTokenClient.DefaultScopes = 'usf-user usf-customer usf-list usf-order usf-product usf-alert usf-payment usf-inventory usf-partner usf-price';
UsfTokenClient.DefaultContext = {
    divisionNumber: 0,
    customerNumber: 0,
    departmentNumber: 0,
};
UsfTokenClient.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: UsfTokenClient, deps: [{ token: i1$4.HttpClient }, { token: PAN_APP_STATE_LIB_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
UsfTokenClient.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: UsfTokenClient, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: UsfTokenClient, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$4.HttpClient }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [PAN_APP_STATE_LIB_CONFIG]
                }] }]; } });

class UsfTokenValidator {
    constructor(config) {
        this.config = config;
    }
    validate(jwt) {
        if (jwt) {
            try {
                return this.validateExpiration(jwt);
            }
            catch (e) {
                console.log('Token: error validating token');
                console.error(e);
                return false;
            }
        }
    }
    validateExpiration(jwt) {
        const decodedJwt = jwtDecode(jwt);
        const expiration = decodedJwt.exp * 1000;
        const now = this.config.tokenServiceSettings.jwtPadding
            ? new Date().getTime() + this.config.tokenServiceSettings.jwtPadding
            : new Date().getTime();
        const valid = expiration > now;
        if (!valid) {
            console.log('Token: access token is expired');
            console.log('Token: date math was ' + expiration + ' > ' + now);
        }
        return valid;
    }
}
UsfTokenValidator.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: UsfTokenValidator, deps: [{ token: PAN_APP_STATE_LIB_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
UsfTokenValidator.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: UsfTokenValidator, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: UsfTokenValidator, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [PAN_APP_STATE_LIB_CONFIG]
                }] }]; } });

class UsfTokenUtils {
    getSessionState(jwt) {
        const decodedJwt = jwtDecode(jwt);
        if (!decodedJwt) {
            throw new Error('Invalid JWT while parsing session');
        }
        const claims = decodedJwt['usf-claims'];
        const isGuest = claims.isGuestUser;
        const sessionState = {
            userName: claims.userName.replace('[AD_AUTO]', ''),
            ecomUserId: claims.userId,
            userType: claims.userType == 'corp-ad' ? UserKinds.Internal : UserKinds.External,
            selectedCustomerId: claims.customerNumber,
            division: claims.divisionNumber,
            department: claims.departmentNumber,
            startTime: new Date().toUTCString(),
            isGuest: isGuest,
        };
        return sessionState;
    }
}
UsfTokenUtils.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: UsfTokenUtils, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
UsfTokenUtils.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: UsfTokenUtils, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: UsfTokenUtils, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

var Customizations;
(function (Customizations) {
    Customizations["DEFAULT"] = "default";
    Customizations["PREMIER"] = "premier";
})(Customizations || (Customizations = {}));

class SiteCustomizationUtils {
    constructor(pageInfoService) {
        this.pageInfoService = pageInfoService;
    }
    /**
     * Some site customizations may be in effect based on the URL or domain prior to use authenitcation
     * @returns The customization that should be applied to the site based on the current URL
     */
    getCustomizationForCurrentUrl() {
        //currently the hostname is sufficient for the logic we need, so just take that.
        const url = this.pageInfoService.getUrlInfo()?.hostName;
        if (!url || typeof url !== 'string') {
            console.error('Unable to determine customization from URL, using default', url);
            return Customizations.DEFAULT;
        }
        if (url && url.indexOf('premierfsdo') > -1) {
            return Customizations.PREMIER;
        }
        //standard usfoods web config
        return Customizations.DEFAULT;
    }
}
SiteCustomizationUtils.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SiteCustomizationUtils, deps: [{ token: PageInfoService }], target: i0.ɵɵFactoryTarget.Injectable });
SiteCustomizationUtils.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SiteCustomizationUtils, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SiteCustomizationUtils, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: PageInfoService }]; } });

class AuthConnectService extends IonicAuth {
    constructor(platform, customizationUtils, config) {
        let ionicAuthConfig;
        if (platform.is('hybrid')) {
            //configure for native mobile
            ionicAuthConfig = config.tokenServiceSettings.ionicAuthMobileOptions;
        }
        else {
            if (
            //if running on a premier fsdo url, use config with correct oauth callbacks
            Customizations.PREMIER ==
                customizationUtils.getCustomizationForCurrentUrl() &&
                config.tokenServiceSettings.ionicAuthPremierWebOptions) {
                ionicAuthConfig =
                    config.tokenServiceSettings.ionicAuthPremierWebOptions;
            }
            else {
                //standard usfoods web config
                ionicAuthConfig = config.tokenServiceSettings.ionicAuthWebOptions;
            }
        }
        super(ionicAuthConfig);
    }
    async login() {
        try {
            localStorage.setItem(LOCAL_STORAGE_KEYS.shouldBlockReload, JSON.stringify(true));
            await super.login();
        }
        catch (err) {
            throw new Error(err.error || err.message);
        }
        finally {
            localStorage.setItem(LOCAL_STORAGE_KEYS.shouldBlockReload, JSON.stringify(false));
        }
    }
}
AuthConnectService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AuthConnectService, deps: [{ token: i1$1.Platform }, { token: SiteCustomizationUtils }, { token: PAN_APP_STATE_LIB_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
AuthConnectService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AuthConnectService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AuthConnectService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$1.Platform }, { type: SiteCustomizationUtils }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [PAN_APP_STATE_LIB_CONFIG]
                }] }]; } });

class UsfTokenService {
    constructor(tokenStorageService, tokenClient, tokenValidator, tokenUtils, authConnectService, store, router, config) {
        this.tokenStorageService = tokenStorageService;
        this.tokenClient = tokenClient;
        this.tokenValidator = tokenValidator;
        this.tokenUtils = tokenUtils;
        this.authConnectService = authConnectService;
        this.store = store;
        this.router = router;
        this.config = config;
        this.refreshInFlight$ = new BehaviorSubject(false);
        this.loginInProgress$ = new BehaviorSubject(false);
    }
    markRefreshInFlight() {
        console.log('[UsfTokenService] Starting token refresh');
        this.refreshInFlight$.next(true);
    }
    markRefreshComplete() {
        console.log('[UsfTokenService] Token refresh complete');
        this.refreshInFlight$.next(false);
    }
    async refresh(context) {
        console.log('[UsfTokenService] Calling refresh()');
        let options;
        return this.refreshInFlight$
            .pipe(
        // block on in flight refresh requests
        filter((val) => val === false), tap(() => {
            console.log('[UsfTokenService] Token refresh in flight', this.refreshInFlight$.getValue());
        }), take(1), switchMap(() => {
            return forkJoin({
                // gather data from storage
                tokens: this.tokenStorageService.getTokens(),
                savedOptions: this.tokenStorageService.getOptions(),
            }).pipe(switchMap(({ tokens, savedOptions }) => {
                options = savedOptions;
                return this.refreshAccessToken(tokens.refreshToken, context, savedOptions); // if not valid, fetch a new one
            }), tap((accessToken) => {
                this.store.dispatch(SESSION_ACTIONS.startSession({
                    session: this.createSessionState(accessToken),
                }));
            }), finalize(() => {
                console.log('[UsfTokenService] Dispatching login actions after customer switch');
                const refreshOptions = {
                    ...options,
                    authContext: context,
                };
                this.store.dispatch(PLATFORM_ACTIONS.newSession({ options: refreshOptions }));
            }));
        }))
            .toPromise()
            .catch(async (e) => {
            this.markRefreshComplete();
            throw e;
        });
    }
    async authorize(mode, options) {
        if (TokenAuthMode.B2C == mode ||
            TokenAuthMode.Punchout == mode ||
            TokenAuthMode.Punchthru == mode ||
            TokenAuthMode.NvPunchthru == mode) {
            return this.authorizeTokenB2COrPunchoutOrPunchthru(mode, options);
        }
        else if (TokenAuthMode.Local == mode) {
            return this.authorizeTokenLocalStorage(options);
        }
        else if (TokenAuthMode.Guest == mode) {
            return this.authorizeGuest(options);
        }
        throw new Error('[UsfTokenService] Authorize must be called with a valid mode');
    }
    async authorizeTokenB2COrPunchoutOrPunchthru(mode, options) {
        return this.tokenClient
            .get(mode, options)
            .pipe(
        // only one thing at a time past here
        shareReplay({ bufferSize: 1, refCount: true }), tap((response) => {
            // persist the response
            this.save(response, options);
            // kick off login actions
            this.store.dispatch(PLATFORM_ACTIONS.newSession({ options }));
            this.store.dispatch(TOKEN_ACTIONS.saveToken({
                token: {
                    accessToken: response.accessToken,
                    refreshToken: response.refreshToken,
                },
            }));
            this.store.dispatch(PLATFORM_ACTIONS.setUserState({
                userFromToken: response,
            }));
        }), map((t) => {
            // only expose the jwt from the response to the subscriber
            return t.accessToken;
        }), tap((accessToken) => {
            this.store.dispatch(SESSION_ACTIONS.startSession({
                session: this.createSessionState(accessToken),
            }));
        }))
            .toPromise()
            .catch(async (e) => {
            console.log('[UsfTokenService] Handling an error from remote auth:', e);
            e?.status === 503
                ? await this.navigateToScheduledMaintenance()
                : await this.navigateToLoginError(e);
            return Promise.reject(e);
        });
    }
    async authorizeTokenLocalStorage(options) {
        const accessToken = this.tokenStorageService.getAccessToken();
        const isValid = this.validate(accessToken);
        // can return early if isValid?
        return this.refreshInFlight$
            .pipe(
        // block on in flight refresh requests
        filter((val) => val === false), take(1), switchMap(() => {
            return isValid
                ? Promise.resolve(accessToken)
                : this.getToken().pipe(map((token) => {
                    this.markRefreshComplete();
                    return token;
                }));
        }))
            .toPromise()
            .catch(async (e) => {
            this.markRefreshComplete();
            console.log('[UsfTokenService] Handling an error from local auth:', e);
            if (e?.status === 503) {
                await this.navigateToScheduledMaintenance();
                return;
            }
            if (options?.disableErrorRedirect == undefined ||
                options?.disableErrorRedirect == false) {
                console.log('[UsfTokenService] Navigating to login from local auth');
                await this.navigateToLogin(options);
            }
            else {
                return Promise.reject(e);
            }
        });
    }
    getGuestToken(options, isRefresh = false) {
        if (isRefresh) {
            console.log('[Guest] Refreshing guest token');
            this.markRefreshInFlight();
        }
        const refreshOptions = {
            ...options,
        };
        return this.tokenClient.get(TokenAuthMode.Guest, refreshOptions).pipe(
        // only one thing at a time past here
        shareReplay({ bufferSize: 1, refCount: true }), tap((response) => {
            // persist the response
            this.save(response, {
                kmsi: true,
                grantType: 'guest',
                zipCode: options?.zipCode,
            });
            // kick off login actions
            this.store.dispatch(PLATFORM_ACTIONS.newSession({})); //does this need options?
            this.store.dispatch(PLATFORM_ACTIONS.setUserState({
                userFromToken: response,
            }));
        }), map((t) => {
            // only expose the jwt from the response to the subscriber
            return t.accessToken;
        }), tap((accessToken) => {
            if (isRefresh)
                this.markRefreshComplete();
            this.store.dispatch(SESSION_ACTIONS.startSession({
                session: this.createSessionState(accessToken),
            }));
        }));
    }
    authorizeGuest(options) {
        console.log('[Guest] Authorize Guest Login');
        return this.getGuestToken(options)
            .toPromise()
            .catch(async (e) => {
            console.log('[Guest Login] Handling an error:', e);
            if (e?.status == 503) {
                await this.navigateToScheduledMaintenance();
            }
            return Promise.reject(e);
        });
    }
    createSessionState(accessToken) {
        return this.tokenUtils.getSessionState(accessToken);
    }
    async navigateToScheduledMaintenance() {
        console.log('[UsfTokenService] Navigating to scheduled maintenance page');
        await this.router.navigate([
            this.config.tokenServiceSettings.scheduledMaintenancePath,
        ]);
    }
    async navigateToLoginError(e) {
        console.log('[UsfTokenService] Navigating to login error page');
        await this.router.navigate([this.config.tokenServiceSettings.loginErrorPath], {
            queryParams: { errorStatus: e?.status },
        });
    }
    // NOTE: some combinations of these options are not valid together (e.g. impersonation and automation)
    async navigateToLogin(options) {
        if (this.loginInProgress$.getValue() === true) {
            console.log('[UsfTokenService] Ignoring navigateToLogin because a login is already in progress');
            return;
        }
        this.loginInProgress$.next(true);
        // if options were not passed in, take what's stored
        options =
            options ?? (await this.tokenStorageService.getOptions().toPromise());
        console.log('[UsfTokenService] Navigating to login with options', options);
        // see if our options require overriding the B2C url
        const overrideDiscoveryUrl = this.getB2CDiscoveryOverride(options);
        // always clear to prevent repeated impersonation b2c logins
        await this.authConnectService.clearOverrideDiscoveryUrl().then(() => {
            if (overrideDiscoveryUrl) {
                console.log('[UsfTokenService] Overriding B2C policy url', overrideDiscoveryUrl);
                this.authConnectService.setOverrideDiscoveryUrl(overrideDiscoveryUrl);
            }
        });
        this.authConnectService.additionalLoginParameters(this.getB2CParams(options));
        this.authConnectService.login().then((_) => {
            this.loginInProgress$.next(false);
            console.log('[UsfTokenService] Finished AuthConnect login');
        }, async (e) => {
            this.loginInProgress$.next(false);
            console.log('[UsfTokenService] Error during AuthConnect login', e);
            await this.handleB2CError(e.message || '');
        });
    }
    // Get the correct B2C policy reference for the given options
    getB2CDiscoveryOverride(options) {
        // test automation
        if (options?.testAutomation) {
            return this.config.tokenServiceSettings.b2cAutomationPolicyUrl;
        }
        // invitationEmail
        if (options?.invitationToken) {
            return this.config.tokenServiceSettings.b2cInvitationLoginPolicyUrl;
        }
        // ccaInvitationEmail
        if (options?.ccaInvitationToken) {
            return this.config.tokenServiceSettings
                .b2cPasswordlessInvitationLoginPolicyUrl;
        }
        // impersonation
        if (options?.impersonationUserId) {
            return this.config.tokenServiceSettings.b2cImpersonationPolicyUrl;
        }
        // no override
        return null;
    }
    getB2CParams(options) {
        const additionalParams = {};
        additionalParams['time'] = new Date().toISOString();
        if (options?.impersonationUserId) {
            additionalParams['targetUsername'] = options.impersonationUserId;
            if (options?.internalUserImpersonation) {
                additionalParams['isInternal'] = 'true';
            }
            if (options?.punchoutUserImpersonation) {
                additionalParams['isPunchout'] = 'true';
            }
        }
        if (options?.invitationToken) {
            additionalParams['id_token_hint'] = options.invitationToken;
        }
        if (options?.ccaInvitationToken) {
            additionalParams['id_token_hint'] = options.ccaInvitationToken;
        }
        return additionalParams;
    }
    async handleB2CCallback(url) {
        console.log('[UsfTokenService] AuthConnect handling login callback', {
            url,
        });
        return this.authConnectService.handleLoginCallback(url).then((resp) => {
            return resp;
        }, (e) => {
            console.log('[UsfTokenService] AuthConnect caught a callback error', e);
            return Promise.reject(e);
        });
    }
    async logout(redirectToRoute = undefined) {
        sessionStorage.clear();
        localStorage.removeItem(LOCAL_STORAGE_KEYS.params);
        localStorage.removeItem(LOCAL_STORAGE_KEYS.promoAppBanner);
        await this.tokenStorageService.clear().toPromise();
        await this.authConnectService.clearStorage();
        if (redirectToRoute) {
            this.router.navigate(redirectToRoute);
        }
        else {
            await this.navigateToLogin({ disableErrorRedirect: true });
        }
    }
    async logoutAllSessions(userName, userType) {
        const token = await this.authorize(TokenAuthMode.Local, {
            disableErrorRedirect: true,
        });
        // TODO: consider including authconnect.logout as well.
        // the client is currently calling both itself
        return this.tokenClient.removeAll(userName, userType, token).toPromise();
    }
    getToken(options) {
        return forkJoin({
            // gather data from storage
            tokens: this.tokenStorageService.getTokens(),
            context: this.tokenStorageService.getContext(),
            savedOptions: this.tokenStorageService.getOptions(),
        }).pipe(shareReplay({ bufferSize: 1, refCount: true }), switchMap(({ tokens, context, savedOptions }) => {
            const refreshContext = options?.authContext ?? context;
            const isGuest = savedOptions?.grantType === 'guest';
            return this.validate(tokens.accessToken)
                ? of(tokens.accessToken)
                : isGuest
                    ? this.getGuestToken(savedOptions, true)
                    : this.refreshAccessToken(tokens.refreshToken, refreshContext, savedOptions);
        }), tap((accessToken) => {
            this.store.dispatch(SESSION_ACTIONS.refreshSession({
                session: this.createSessionState(accessToken),
            }));
        }), catchError((e) => {
            return throwError(e);
        }));
    }
    refreshAccessToken(refreshToken, context, options) {
        console.log('[UsfTokenService] Calling refreshAccessToken()');
        if (!refreshToken) {
            throw new Error('[UsfTokenService] Refresh token cannot be empty');
        }
        this.markRefreshInFlight();
        const mode = TokenAuthMode.Refresh;
        const refreshOptions = {
            ...options,
            authContext: context,
            refreshToken: refreshToken,
        };
        console.log('[UsfTokenService] Refreshing auth with token: ' + refreshToken);
        return this.tokenClient.get(mode, refreshOptions).pipe(tap((response) => {
            // persist the response
            this.save(response, refreshOptions);
            this.store.dispatch(PLATFORM_ACTIONS.setUserState({ userFromToken: response }));
        }), map((t) => {
            this.markRefreshComplete();
            // only expose the jwt from the response to the subscriber
            return t.accessToken;
        }), catchError((e) => {
            this.markRefreshComplete();
            return throwError(e);
        }));
    }
    validate(token) {
        try {
            return this.tokenValidator.validate(token);
        }
        catch (e) {
            console.log('[UsfTokenService] Error validating access token', e);
        }
        return false;
    }
    // trigger the effects in client lib for setting up websockets etc
    dispatch() {
        console.log('[UsfTokenService] Dispatching login actions');
        return forkJoin({
            // gather data from storage
            savedOptions: this.tokenStorageService.getOptions(),
            response: this.tokenStorageService.getResponse(),
        }).pipe(take(1), tap(({ response, savedOptions }) => {
            if (response) {
                this.store.dispatch(PLATFORM_ACTIONS.newSession({ options: savedOptions }));
                this.store.dispatch(PLATFORM_ACTIONS.setUserState({ userFromToken: response }));
            }
        }));
    }
    save(response, options) {
        console.log('[UsfTokenService] Saving data from auth api response' + '\n', response);
        const tokens = {
            accessToken: response.accessToken,
            refreshToken: response.refreshToken,
        };
        const context = {
            divisionNumber: response.divisionNumber,
            customerNumber: response.customerNumber,
            departmentNumber: response.departmentNumber,
        };
        const newOptions = {
            ...options,
        };
        newOptions.refreshToken = tokens.refreshToken;
        // TODO: handle potential errors from storage
        this.tokenStorageService.saveTokens(tokens, options);
        this.tokenStorageService.saveContext(context);
        this.tokenStorageService.saveOptions(newOptions);
        this.tokenStorageService.saveResponse(response);
        if (response.punchoutSession) {
            const punchoutSession = response.punchoutSession;
            this.tokenStorageService.savePunchoutSession(punchoutSession);
        }
        if (response.punchthruSession) {
            const punchthruSession = response.punchthruSession;
            this.tokenStorageService.savePunchthruSession(punchthruSession);
        }
        this.store.dispatch(TOKEN_ACTIONS.saveToken({
            token: {
                accessToken: response.accessToken,
                refreshToken: response.refreshToken,
            },
        }));
    }
    /*
    duplicated from client.  ionic authconnect client handles errors inconsistently between web and mobile.
  
    - natively, the library returns a rejected promise
    - in web, it succeeds and lets the oauth return URL carry the error
  
    this could be deduplicated/refactored a couple of ways, but ionic lib is in need of update and this behavior
    is likely to have changed based on the release notes.
    */
    async handleB2CError(error) {
        if (error.startsWith('AAD_Custom_Error_SessionRevoked')) {
            await this.logout();
            return;
        }
        if (error.startsWith('AAD_Custom_Error_ExpiredLoginPage')) {
            await this.logout();
            return;
        }
        // TODO: validate this is needed.  appears to be copied from the Ionic docs
        // Handle the password reset case for Azure AD
        if (error.startsWith('AADB2C90118')) {
            await this.logout();
            return;
        }
        if (error.startsWith('No session data stored')) {
            await this.logout();
            return;
        }
        if (error.startsWith('AAD_Custom_Error_RequirePasswordAndSkipMfa')) {
            console.log('[TokenService] Navigating to login for automation');
            await this.router.navigate(['/login'], {
                queryParams: { automation: true },
            });
            return;
        }
        console.error('An unspecified error has occurred during login', error);
        await this.router.navigate(['/login-error']);
        return;
    }
}
UsfTokenService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: UsfTokenService, deps: [{ token: UsfTokenStorageService }, { token: UsfTokenClient }, { token: UsfTokenValidator }, { token: UsfTokenUtils }, { token: AuthConnectService }, { token: i2.Store }, { token: i1.Router }, { token: PAN_APP_STATE_LIB_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
UsfTokenService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: UsfTokenService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: UsfTokenService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: UsfTokenStorageService }, { type: UsfTokenClient }, { type: UsfTokenValidator }, { type: UsfTokenUtils }, { type: AuthConnectService }, { type: i2.Store }, { type: i1.Router }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [PAN_APP_STATE_LIB_CONFIG]
                }] }]; } });

/** OpenTelemetryInjectConfig : Config injection */
const OPEN_TELEMETRY_INJECT_CONFIG = new InjectionToken('opentelemetry.config');
/** Logger : injection for a logger compatible */
const OTELCOL_LOGGER = new InjectionToken('otelcol.logger');
/** custom span */
const CUSTOM_SPAN = new InjectionToken('otelcol.custom-span');

const transformHelper = (preTransformed) => ({
    action: preTransformed.action,
    previousState: preTransformed.previousState,
    resultState: preTransformed.resultState,
    data: preTransformed.action.tracking.analytics.data,
});
const tracingTransformHelper = (preTransformed) => ({
    action: preTransformed.action,
    previousState: preTransformed.previousState,
    resultState: preTransformed.resultState,
    data: preTransformed.action.tracking.tracing.data,
});
const loggingTransformHelper = (preTransformed) => ({
    action: preTransformed.action,
    previousState: preTransformed.previousState,
    resultState: preTransformed.resultState,
    data: preTransformed.action.tracking.log.data,
});
const getIpAddressFromToken = (token) => {
    const decodedToken = jwtDecode(token);
    const ipAddressInToken = decodedToken?.user?.ipAddress ?? '';
    let ipAddress = '';
    if (ipAddressInToken) {
        const ipArray = ipAddressInToken.split('.');
        ipArray.pop();
        ipAddress = ipArray.join('.') + '.XX';
    }
    return ipAddress ?? '';
};
const browserNameAndVersion = () => {
    const userAgent = navigator.userAgent;
    let browser = '';
    if (userAgent.match(/chrome|chromium|crios/i)) {
        const version = userAgent.split('Chrome/')[1];
        const versionNumber = version?.split(' ')[0] ?? '';
        browser = versionNumber ? `chrome v${versionNumber}` : `chrome`;
    }
    else if (userAgent.match(/firefox|fxios/i)) {
        const version = userAgent.split('Firefox/')[1];
        browser = `firefox v${version}`;
    }
    else if (userAgent.match(/safari/i)) {
        const version = userAgent.split('Safari/')[1];
        browser = `safari v${version}`;
    }
    else if (userAgent.match(/edg/i)) {
        const version = userAgent.split('Edge/')[1];
        browser = `edge v${version}`;
    }
    else {
        browser = '';
    }
    return browser;
};
const formatForOtel = (dataToFormat = {}) => {
    let traceData = {};
    Object.keys(dataToFormat).forEach((parentKey) => {
        const value = dataToFormat[parentKey];
        const valueType = value?.constructor?.name;
        if (valueTypeIsObject(valueType)) {
            Object.keys(value).forEach((childKey) => {
                const childValue = value[childKey];
                const childValueType = childValue?.constructor?.name;
                const parentChildKey = createParentChildKey(parentKey, childKey);
                traceData = assignValueToTraceData(traceData, parentChildKey, childValue, childValueType);
            });
        }
        else {
            traceData = assignValueToTraceData(traceData, parentKey, value, valueType);
        }
    });
    return traceData;
};
const assignValueToTraceData = (traceData, key, value, valueType) => {
    if (valueTypeIsStringOrNumber(valueType)) {
        traceData[key] = value;
    }
    else {
        traceData[key] = JSON.stringify(value);
    }
    return traceData;
};
const createParentChildKey = (parentKey, childKey) => parentKey + '.' + childKey;
const valueTypeIsObject = (valueType) => valueType === 'Object';
const valueTypeIsStringOrNumber = (valueType) => valueType === 'String' || valueType === 'Number';

const defaultAnalyticsTransform = (tracking) => ({
    ...applicationTransform(tracking),
    ...userTransform(tracking),
    ...customerTransform(tracking),
    ...previousPageTransform(tracking),
    ...languageSettingTransform(tracking),
    ...pageTrackingTransform(tracking),
    ...searchTransform(tracking),
    event: tracking.data.event,
});
const defaultTracingTransform = (tracing) => ({
    ...applicationTransform(tracing),
    ...userTracingTransform(tracing),
    ...customerTransform(tracing),
    ...tracingPageTransform(tracing),
    ...platformTransform(tracing),
    ...{ action: tracing.action.type },
});
const defaultLoggingTransform = (logData) => ({
    ...applicationTransform(logData),
    ...userLoggingTransform(logData),
    ...customerTransform(logData),
    ...loggingDataTransform(logData),
    ...loggingPageTransform(logData),
    ...{ action: logData.action.type },
});
const loggingDataTransform = (incomingData) => {
    const id = incomingData.data.message.id || '';
    const type = incomingData.data.message.type.toString() || '';
    const message = incomingData.data.message.display || '';
    const stack = incomingData.data.message.stack || '';
    const watermark = incomingData.data.message.watermark || '';
    const transformedData = {
        logInfo: {
            ...(id && { id }),
            ...(type && { type }),
            ...(stack && { stack }),
            ...(watermark && { watermark }),
            ...(message && { message }),
        },
    };
    return transformedData;
};
const userTransform = (incomingData) => {
    const ecomUserId = incomingData.resultState['app-state'].loggedInUser?.ecomUserId ?? '';
    const userType = incomingData.resultState['app-state'].loggedInUser?.userKind?.toLowerCase() ?? '';
    const impersonatorID = incomingData.resultState['app-state'].loggedInUser?.impersonatorID ?? '';
    if (!ecomUserId) {
        return null;
    }
    const superUserFlag = getSuperUserFlag(incomingData.resultState['app-state'].loggedInUser ?? {});
    const isPunchout = incomingData.resultState['user-state']?.user?.punchOut;
    const punchoutPartnerId = incomingData.resultState['app-state']?.loggedInUser?.partnerId;
    const transformedData = {
        user: {
            ...(ecomUserId && { ecomUserId }),
            ...(userType && { userType }),
            ...(impersonatorID && { impersonatorID }),
            ...(isPunchout && { punchoutPartnerId }),
            ...(superUserFlag && { superUserFlag }),
        },
    };
    return transformedData;
};
const userTracingTransform = (incomingData) => {
    const impersonatorID = incomingData.resultState['app-state'].loggedInUser?.impersonatorID ?? '';
    const id = incomingData.resultState['app-state'].loggedInUser?.userId ?? '';
    const userType = incomingData.resultState['app-state'].loggedInUser?.userKind?.toLowerCase() ?? '';
    const ipAddress = getIpAddressFromToken(incomingData.resultState['app-state'].token?.accessToken) ?? '';
    const superUserFlag = getSuperUserFlag(incomingData.resultState['app-state'].loggedInUser ?? {});
    const isPunchout = incomingData.resultState['user-state']?.user?.punchOut;
    const punchoutPartnerId = incomingData.resultState['app-state']?.loggedInUser?.partnerId;
    const transformedData = {
        user: {
            ...(id && { id }),
            ...(userType && { userType }),
            ...(impersonatorID && { impersonatorID }),
            ...(ipAddress && { ipAddress }),
            ...(isPunchout && { punchoutPartnerId }),
            ...(superUserFlag && { superUserFlag }),
        },
    };
    return transformedData;
};
const userLoggingTransform = (incomingData) => {
    const impersonatorID = incomingData.resultState['app-state'].loggedInUser?.impersonatorID ?? '';
    const id = incomingData.resultState['app-state'].loggedInUser?.userId ?? '';
    const userType = incomingData.resultState['app-state'].loggedInUser?.userKind?.toLowerCase() ?? '';
    const ipAddress = getIpAddressFromToken(incomingData.resultState['app-state'].token?.accessToken) ?? '';
    const superUserFlag = getSuperUserFlag(incomingData.resultState['app-state'].loggedInUser ?? {});
    const isPunchout = incomingData.resultState['user-state']?.user?.punchOut;
    const punchoutPartnerId = incomingData.resultState['app-state']?.loggedInUser?.partnerId;
    const transformedData = {
        user: {
            ...(id && { id }),
            ...(userType && { userType }),
            ...(impersonatorID && { impersonatorID }),
            ...(ipAddress && { ipAddress }),
            ...(isPunchout && { punchoutPartnerId }),
            ...(superUserFlag && { superUserFlag }),
        },
    };
    return transformedData;
};
const getSuperUserFlag = (loggedInUser) => {
    const userProfiles = loggedInUser?.profiles ?? [];
    const superUserProfile = userProfiles?.find((userProf) => userProf.key === 'SUPER_USER');
    if (!!superUserProfile && superUserProfile?.value === 'Y') {
        return '1';
    }
    else {
        return undefined;
    }
};
const customerTransform = (incomingData) => {
    const divisionNumber = incomingData.resultState['app-state'].selectedCustomer?.divisionNumber ??
        '';
    const customerNumber = incomingData.resultState['app-state'].selectedCustomer?.customerNumber ??
        '';
    const departmentNumber = incomingData.resultState['app-state'].selectedCustomer?.departmentNumber ??
        '';
    if (!divisionNumber) {
        return null;
    }
    else {
        const transformedData = {
            customer: {
                ...(divisionNumber && { divisionNumber }),
                ...(customerNumber && { customerNumber }),
                ...(departmentNumber && { departmentNumber }),
            },
        };
        return transformedData;
    }
};
const previousPageTransform = (incomingData) => {
    // add previous page object for page load events only
    let pageName = '';
    if (incomingData.data.event !== 'page loaded') {
        return null;
    }
    else {
        const analyticsEvents = window['appEventData']?.filter((data) => data?.event === 'page loaded') ??
            [];
        pageName =
            analyticsEvents.length > 0
                ? analyticsEvents[analyticsEvents.length - 1]?.page?.pageName
                : '';
        const previousUrl = urlOrigin() + incomingData.resultState['app-state'].previousUrl ??
            'unknown';
        const transformedData = {
            previousPage: {
                ...(pageName && { pageName }),
                ...(previousUrl && { previousUrl }),
            },
        };
        return transformedData;
    }
};
const languageSettingTransform = (incomingData) => {
    if (incomingData.data.event !== 'page loaded') {
        return null;
    }
    else {
        const transformedData = {
            languageSetting: localStorage.getItem(LOCAL_STORAGE_KEYS.displayLanguage) === 'es'
                ? 'Spanish'
                : 'English',
        };
        return transformedData;
    }
};
const navigationTransform = (incomingData) => {
    const analyticsEvents = window['appEventData'] ?? [];
    const currentPageName = incomingData.action.tracking.analytics.data.pageName ?? '';
    const previousPageName = analyticsEvents.length > 0
        ? analyticsEvents[analyticsEvents.length - 1].page.pageName
        : '';
    const previousUrl = urlOrigin() + incomingData.resultState['app-state'].previousUrl ??
        'unknown';
    const currentUrl = urlOrigin() + incomingData.resultState['app-state'].currentUrl ?? '';
    const transformedData = {
        navigation: {
            ...(currentUrl && { currentUrl }),
            ...(previousUrl && { previousUrl }),
            ...(currentPageName && { currentPageName }),
            ...(previousPageName && { previousPageName }),
        },
    };
    return transformedData;
};
const platformTransform = (incomingData) => {
    const width = incomingData.resultState['app-state'].platform?.width ?? '';
    const height = incomingData.resultState['app-state'].platform?.height ?? '';
    const isLandscape = incomingData.resultState['app-state'].platform?.isLandscape ?? false;
    const isPortrait = incomingData.resultState['app-state'].platform?.isPortrait ?? false;
    const context = incomingData.resultState['app-state'].platform?.context ?? '';
    const platform = incomingData.resultState['app-state'].platform?.platform?.toLowerCase() ??
        '';
    const operatingSystem = incomingData.resultState['app-state'].platform?.operatingSystem ?? '';
    const osVersion = incomingData.resultState['app-state'].platform?.osVersion ?? '';
    const browserManufacturer = incomingData.resultState['app-state'].platform?.browserManufacturer ?? '';
    const browserVersion = browserNameAndVersion();
    const model = incomingData.resultState['app-state'].platform?.model ?? '';
    const transformedData = {
        platform: {
            ...(width && { width }),
            ...(height && { height }),
            ...(isLandscape && { isLandscape }),
            ...(isPortrait && { isPortrait }),
            ...(context && { context }),
            ...(platform && { platform }),
            ...(operatingSystem && { operatingSystem }),
            ...(osVersion && { osVersion }),
            ...(browserManufacturer && { browserManufacturer }),
            ...(browserVersion && { browserVersion }),
            ...(model && { model }),
        },
    };
    return transformedData;
};
const applicationTransform = (incomingData) => {
    const context = incomingData.resultState['app-state'].platform?.context ?? '';
    const originType = incomingData.resultState['app-state'].originType ?? '';
    const data = {
        application: {
            brand: 'us foods',
            name: 'r4',
            ...(context && { context }),
            ...(originType && { originType }),
        },
    };
    return data;
};
const tracingPageTransform = (incomingData) => {
    const pageURL = window.location.href;
    const transformedData = {
        page: {
            ...(pageURL && { pageURL }),
        },
    };
    return transformedData;
};
const loggingPageTransform = (incomingData) => {
    const pageURL = window.location.href;
    const transformedData = {
        page: {
            ...(pageURL && { pageURL }),
        },
    };
    return transformedData;
};
const pageTrackingTransform = (incomingData) => {
    const linkSource = incomingData.resultState['app-state'].linkSource ?? '';
    if (!linkSource) {
        return null;
    }
    else {
        const transformedData = {
            page: {
                ...(linkSource && { linkSource }),
            },
        };
        return transformedData;
    }
};
const searchTransform = (incomingData) => {
    const rank = incomingData.resultState['app-state'].rank ?? '';
    if (!rank) {
        return null;
    }
    else {
        const transformedData = {
            search: {
                ...(rank && { rank }),
            },
        };
        return transformedData;
    }
};
const usabillaTransform = (incomingData) => {
    const customerData = customerTransform(incomingData);
    const userData = userTransform(incomingData);
    delete userData.user.ecomUserId;
    const usabillaData = {
        ...userData.user,
        ...customerData.customer,
    };
    return usabillaData;
};
const urlOrigin = () => window.location.origin;

const initialAppState = {
    token: null,
    loggedInUser: null,
    selectedCustomer: null,
    beta: false,
    online: false,
    pageTitle: '',
    currentUrl: '',
    logrocketUrl: '',
    previousUrl: '',
    features: {},
    platform: null,
    backgroundTime: null,
    session: null,
    minimumVersionsLoaded: false,
    minimumVersions: {
        android: '',
        ios: '',
    },
};
const _appStateReducer = createReducer(initialAppState, 
/* CUSTOMER ACTIONS */
on(CUSTOMER_ACTIONS.loadSelectedCustomerSuccess, (state, { selectedCustomer }) => ({ ...state, selectedCustomer })), 
/* FEATURE FLAG ACTIONS */
on(FEATURE_FLAG_ACTIONS.getSplitTreatmentsSuccess, (state, { splits }) => {
    const features = Object.assign({ ...state.features, ...splits });
    return { ...state, features };
}), 
/* MINIMUM VERSION ACTIONS */
on(MINIMUM_VERSIONS_ACTIONS.getMinimumVersions, (state) => {
    return {
        ...state,
        minimumVersionsLoaded: false,
    };
}), on(MINIMUM_VERSIONS_ACTIONS.getMinimumVersionsFail, (state, action) => {
    return {
        ...state,
        minimumVersionsLoaded: true,
        error: action.minimumVersionsError,
    };
}), on(MINIMUM_VERSIONS_ACTIONS.setMinimumVersions, (state, action) => {
    return {
        ...state,
        minimumVersionsLoaded: true,
        minimumVersions: action.minimumVersions,
    };
}), 
/* NETWORK ACTIONS */
on(NETWORK_ACTIONS.setStatus, (state, { isConnected }) => ({
    ...state,
    online: isConnected,
})), on(NETWORK_ACTIONS.setOnline, (state) => ({ ...state, online: true })), on(NETWORK_ACTIONS.setOffline, (state) => ({ ...state, online: false })), 
/* PAGE ACTIONS */
on(PAGE_ACTIONS.setPageTitle, (state, action) => ({
    ...state,
    pageTitle: action.pageTitle,
})), on(PAGE_ACTIONS.setCurrentUrl, (state, action) => ({
    ...state,
    currentUrl: action.currentUrl,
})), on(PAGE_ACTIONS.setPreviousUrl, (state, action) => ({
    ...state,
    previousUrl: action.previousUrl,
})), on(PAGE_ACTIONS.setOriginType, (state, action) => ({
    ...state,
    originType: action.originType,
})), on(PAGE_ACTIONS.setLinkSource, (state, action) => ({
    ...state,
    linkSource: action.linkSource,
})), on(PAGE_ACTIONS.setLogrocketUrl, (state, action) => ({
    ...state,
    logrocketUrl: action.url,
})), on(PAGE_ACTIONS.setRank, (state, action) => ({
    ...state,
    rank: action.rank,
})), 
/* PLATFORM ACTIONS */
on(PLATFORM_ACTIONS.setPlatform, (state, action) => ({
    ...state,
    platform: action.platform,
})), on(PLATFORM_ACTIONS.setBrowserDimensions, (state, action) => {
    const platform = Object.assign({}, { ...state.platform, ...action.dimensions });
    return {
        ...state,
        platform,
    };
}), on(PLATFORM_ACTIONS.appBackground, (state, { backgroundTime }) => {
    return { ...state, backgroundTime };
}), 
/* SESSION ACTIONS */
on(SESSION_ACTIONS.startSession, (state, { session }) => ({
    ...state,
    session,
})), on(SESSION_ACTIONS.refreshSession, (state, { session }) => {
    const newSession = Object.assign({
        ...session,
        startTime: state.session?.startTime || session.startTime,
    });
    return {
        ...state,
        session: { ...newSession },
    };
}), 
/* TOKEN ACTIONS */
on(TOKEN_ACTIONS.loadTokenStateSuccess, (state, { token }) => ({
    ...state,
    token,
})), on(TOKEN_ACTIONS.clearTokenState, (state) => ({
    ...state,
    token: null,
    loggedInUser: null,
    selectedCustomer: null,
})), on(TOKEN_ACTIONS.clearStore, (state) => initialAppState), on(TOKEN_ACTIONS.saveToken, (state, { token }) => ({
    ...state,
    token,
})), 
/* USER ACTIONS */
on(USER_ACTIONS.loadUserSuccess, (state, { loggedInUser }) => {
    const user = { ...state.loggedInUser, ...loggedInUser };
    return {
        ...state,
        loggedInUser: user,
    };
}), on(USER_ACTIONS.loadAssignedCustomers, (state, { assignedCustomers }) => {
    const loggedInUser = Object.assign({
        ...state.loggedInUser,
        assignedCustomers,
    });
    return { ...state, loggedInUser };
}), on(USER_ACTIONS.loadProfileSettings, (state, { profileSettings }) => {
    const loggedInUser = Object.assign({
        ...state.loggedInUser,
        profiles: profileSettings,
    });
    return { ...state, loggedInUser };
}));
function appStateReducer(state, action) {
    const result = _appStateReducer(state, action);
    // persist app state here
    // determine if action is an app-state action
    return result;
}

const tracker = (trackingService) => (reducer) => (state, action) => {
    const trackingAction = action;
    const tracking = trackingAction.tracking || false;
    const result = reducer(state, action);
    /**
     * START OF WEB ANALYTICS PROCCESSING
     *
     * @Summary
     * Responsible for creating the data layer and sending the data up
     * to 3rd party Web Analytics provider
     */
    if (tracking && tracking.hasOwnProperty('analytics')) {
        // Make the data easier to transform
        const trackingData = transformHelper({
            action: trackingAction,
            previousState: state,
            resultState: result,
            data: trackingAction.tracking.analytics.data,
        });
        // Create the common data layer (ex. application, user, customer)
        const commonDataLayer = defaultAnalyticsTransform(trackingData);
        // check if transformFunc is undefined
        // if so, return commonDataLayer
        if (!tracking.analytics.transformFunc) {
            trackingService.sendWebAnalytics({ ...commonDataLayer });
        }
        else {
            const dataLayer = (data) => tracking.analytics.transformFunc(data);
            // save result from transform function
            const res = dataLayer(trackingData);
            //if both have page object, merge it first
            if (commonDataLayer.page) {
                res.page = { ...res.page, ...commonDataLayer.page };
                delete commonDataLayer.page;
            }
            //if both have search object, merge it first
            if (commonDataLayer.search) {
                res.search = { ...res.search, ...commonDataLayer.search };
                delete commonDataLayer.search;
            }
            if (trackingService.trackEnv !== 'prod') {
                console.log('DATA LAYER:  ', { ...res, ...commonDataLayer });
            }
            trackingService.sendWebAnalytics({ ...res, ...commonDataLayer });
        }
    }
    if (tracking && tracking.hasOwnProperty('tracing')) {
        // Make the data easier to transform
        const tracingData = tracingTransformHelper({
            action: trackingAction,
            previousState: state,
            resultState: result,
            data: trackingAction.tracking.tracing.data,
        });
        // Create the common data layer (ex. application, user, customer)
        const commonDataLayer = defaultTracingTransform(tracingData);
        if (!tracking?.tracing?.transformFunc) {
            if (trackingService.trackEnv !== 'prod') {
                console.log('TRACING DATA:  ', formatForOtel(commonDataLayer));
            }
            trackingService.sendTracing(tracingData, formatForOtel(commonDataLayer));
        }
        else {
            // assigns pointer function to local var
            const dataLayer = (data) => tracking.tracing.transformFunc(data);
            // save result from transform function
            const res = dataLayer(tracingData);
            const attributes = formatForOtel({
                ...res,
                ...commonDataLayer,
            });
            if (trackingService.trackEnv !== 'prod') {
                console.log('TRACING DATA:  ', attributes);
            }
            trackingService.sendTracing(tracingData, attributes);
        }
    }
    if (tracking && tracking.hasOwnProperty('log')) {
        // Make the data easier to transform
        const logData = loggingTransformHelper({
            action: trackingAction,
            previousState: state,
            resultState: result,
            data: trackingAction.tracking.log.data,
        });
        // Create the common data layer (ex. application, user, customer)
        const commonDataLayer = defaultLoggingTransform(logData);
        if (!tracking?.tracing?.transformFunc) {
            if (trackingService.trackEnv !== 'prod') {
                console.log('OTEL LOG DATA:  ', formatForOtel(commonDataLayer));
            }
            trackingService.sendLogs(logData, formatForOtel(commonDataLayer));
        }
        else {
            // assigns pointer function to local var
            const dataLayer = (data) => tracking.log.transformFunc(data);
            // save result from transform function
            const res = dataLayer(logData);
            const attributes = formatForOtel({
                ...res,
                ...commonDataLayer,
            });
            if (trackingService.trackEnv !== 'prod') {
                console.log('OTEL LOG DATA:  ', attributes);
            }
            trackingService.sendLogs(logData, attributes);
        }
    }
    if (tracking && tracking.hasOwnProperty('usabilla')) {
        const trackingData = transformHelper({
            action: trackingAction,
            previousState: state,
            resultState: result,
        });
        // Create the common data layer (ex. application, user, customer)
        const commonDataLayer = usabillaTransform(trackingData);
        trackingService.triggerCustomFeedback(trackingAction.tracking.usabilla.data.campaignName, {
            ...commonDataLayer,
            ...trackingAction.tracking.usabilla.data.customData,
        });
    }
    return result;
};

class PlatformInitializationService {
    constructor(appState, tracingService, webSocketService, usabillaService, tokenStorageService, tokenUtils, config) {
        this.appState = appState;
        this.tracingService = tracingService;
        this.webSocketService = webSocketService;
        this.usabillaService = usabillaService;
        this.tokenStorageService = tokenStorageService;
        this.tokenUtils = tokenUtils;
        this.config = config;
    }
    initialize() {
        return new Promise((resolve, reject) => {
            console.log('[Platform] Initializing');
            if (this.config.featureFlagSettings) {
                this.appState.initializeFeatureFlags();
            }
            if (this.config.openTelemetrySettings) {
                this.tracingService.initializeProviderAndContext(this.config.openTelemetrySettings);
            }
            this.setUpWebAnalytics();
            this.injectScript(this.config.logRocketSettings.logRocketScriptSrc, 'logRocketInit');
            this.initSession();
            this.initWebsockets();
            this.initLogrocket();
            this.initUsabilla();
            resolve();
        });
    }
    initSession() {
        console.log('[Platform] Initializing session');
        //if we already have tokens stored,
        //use them to start a session and seed data in state
        this.tokenStorageService
            .getTokens()
            .pipe(take(1))
            .subscribe((tokenSet) => {
            if (tokenSet?.accessToken && tokenSet?.refreshToken) {
                console.log('[Platform] Seeding token state from storage', tokenSet);
                this.appState.persistTokenState({
                    accessToken: tokenSet.accessToken,
                    refreshToken: tokenSet.refreshToken,
                });
                const sessionState = this.tokenUtils.getSessionState(tokenSet.accessToken);
                console.log('[Platform] Seeding session state from storage', sessionState);
                this.appState.startSession(sessionState);
            }
        });
    }
    initUsabilla() {
        if (!Capacitor.isNativePlatform()) {
            this.usabillaService.injectUsabillaJS(this.config.usabillaSettings.scriptSrc, this.config.usabillaSettings.scriptId);
            combineLatest([
                this.appState.session$,
                this.appState.currentUrl$,
                this.appState.logrocketUrl$,
            ]).subscribe(([session, currentUrl, logrocketUrl]) => {
                const data = {
                    feedbackUrl: currentUrl || '',
                    logrocketUrl: logrocketUrl?.toString() || '',
                    userName: session.userName || '',
                    userType: session.userType || '',
                    ecomUserId: session.ecomUserId?.toString() || '',
                    buildNumber: this.config.buildNumber || '',
                    divisionNumber: session.division?.toString() || '',
                    customerNumber: session.selectedCustomerId?.toString() || '',
                    departmentNumber: session.department?.toString() || '',
                };
                this.usabillaService.setCustomVariables(data);
            });
        }
    }
    initWebsockets() {
        console.log('Initializing WebSockets');
        this.appState.accessToken$.subscribe((accessToken) => {
            console.log('[Platform] Connecting websockets with token', {
                token: accessToken,
            });
            this.webSocketService.closeConnection();
            this.webSocketService.connect(accessToken, 'OnRegistration');
        });
    }
    initLogrocket() {
        this.featureFlagLogRocketSub$ = this.appState
            .feature$(['split_global_logoutrocket_sessions'])
            .subscribe(async (on) => {
            if (on) {
                console.log('Initializing logrocket client');
                const settings = this.config.logRocketSettings;
                const fuzzySanitizer = this.createSanitizers();
                LogRocket__default.init(settings.logRocketInit, {
                    dom: {
                        baseHref: settings.logRocketBaseHref,
                    },
                    release: this.config.buildNumber?.toString() || '',
                    network: {
                        requestSanitizer: (request) => this.sanitizeSensitiveFields(fuzzySanitizer.requestSanitizer, request),
                        responseSanitizer: (response) => this.sanitizeSensitiveFields(fuzzySanitizer.responseSanitizer, response),
                    },
                });
                this.identifyLogrocketUser();
                this.appState.getLogRocketUrl();
            }
        });
    }
    createSanitizers() {
        return LogrocketFuzzySanitizer.setup(this.config.logRocketSettings.logRocketSanitizeRequestFields);
    }
    sanitizeSensitiveFields(sanitizer, httpOperation) {
        let sanitizedResponse = { ...httpOperation };
        let fuzzyObject = {
            body: sanitizedResponse.body,
            method: sanitizedResponse.method,
            headers: sanitizedResponse.headers,
        };
        let fuzzySanitized = sanitizer(fuzzyObject);
        return {
            ...sanitizedResponse,
            ...fuzzySanitized,
        };
    }
    injectLogrocketScript(scriptSrc) {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = scriptSrc;
        script.id = 'logRocketInit';
        script.async = false;
        document.getElementsByTagName('head')[0].appendChild(script);
    }
    identifyLogrocketUser() {
        combineLatest([
            this.appState.feature$(['split_global_logoutrocket_sessions']),
            this.appState.user$
        ])
            .pipe(filter(([flagOn, user]) => flagOn && user && !!user.userId), take(1))
            .subscribe(([flagOn, user]) => {
            console.log('Identifying logrocket user: ', user.userId);
            LogRocket__default.identify(user.userId);
        });
    }
    setUpWebAnalytics() {
        if (this.config.webAnalyticsSettings) {
            const settings = this.config.webAnalyticsSettings;
            this.injectScript(settings.scriptSrc, settings.scriptId);
        }
    }
    injectScript(scriptSrc, scriptId) {
        const hasScript = !!document.getElementById(scriptId);
        if (!hasScript) {
            const script = document.createElement('script');
            script.type = 'text/javascript';
            script.src = scriptSrc;
            script.id = scriptId;
            script.async = true;
            document.getElementsByTagName('head')[0].appendChild(script);
        }
    }
}
PlatformInitializationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PlatformInitializationService, deps: [{ token: PanAppState }, { token: TracingService }, { token: i2$2.WebSocketService }, { token: UsabillaService }, { token: UsfTokenStorageService }, { token: UsfTokenUtils }, { token: PAN_APP_STATE_LIB_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
PlatformInitializationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PlatformInitializationService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PlatformInitializationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: PanAppState }, { type: TracingService }, { type: i2$2.WebSocketService }, { type: UsabillaService }, { type: UsfTokenStorageService }, { type: UsfTokenUtils }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [PAN_APP_STATE_LIB_CONFIG]
                }] }]; } });

class PlatformEffects {
    constructor(actions$, webSocketService, usfTokenService, panAppState, platformInitializationService, config) {
        this.actions$ = actions$;
        this.webSocketService = webSocketService;
        this.usfTokenService = usfTokenService;
        this.panAppState = panAppState;
        this.platformInitializationService = platformInitializationService;
        this.config = config;
        this.appForeground$ = createEffect(() => this.actions$.pipe(ofType(PLATFORM_ACTIONS.appForeground), withLatestFrom(this.webSocketService.isConnected$, this.panAppState.feature$(['split_user_websocket_reconnect'])), tap(async ([action, isConnected, websocketFlag]) => {
            if (websocketFlag) {
                this.webSocketService.cancelDelay();
                if (!isConnected) {
                    try {
                        const accessToken = await this.usfTokenService.authorize(TokenAuthMode.Local, { disableErrorRedirect: true });
                        this.webSocketService.connect(accessToken, 'OnRegistration');
                    }
                    catch (e) {
                        console.log('Unable to connect websockets', e.message);
                    }
                }
            }
        })), { dispatch: false });
        this.appBackground$ = createEffect(() => this.actions$.pipe(ofType(PLATFORM_ACTIONS.appBackground), withLatestFrom(this.panAppState.feature$(['split_user_websocket_reconnect'])), tap(([action, websocketFlag]) => {
            if (websocketFlag) {
                this.webSocketService.closeConnectionWithDelay(15000);
            }
        })), { dispatch: false });
        this.setUserState$ = createEffect(() => this.actions$.pipe(ofType(PLATFORM_ACTIONS.setUserState), concatMap((action) => {
            return [
                USER_ACTIONS.loadUserSuccess({
                    loggedInUser: this.getLoggedInUserFromToken(action.userFromToken.accessToken),
                }),
            ];
        })));
        this.startSession$ = createEffect(() => this.actions$.pipe(ofType(SESSION_ACTIONS.startSession), tap((action) => {
            console.log('[Session] Started', action);
            this.configureNewRelicAttributes(action.session);
            this.platformInitializationService.identifyLogrocketUser();
            this.panAppState.getLogRocketUrl();
        })), { dispatch: false });
        this.refreshSession$ = createEffect(() => this.actions$.pipe(ofType(SESSION_ACTIONS.refreshSession), tap((action) => {
            console.log('[Session] Refresh', action);
            this.configureNewRelicAttributes(action.session);
            this.platformInitializationService.identifyLogrocketUser();
            this.panAppState.getLogRocketUrl();
        })), { dispatch: false });
        this.endSession$ = createEffect(() => this.actions$.pipe(ofType(SESSION_ACTIONS.endSession), tap((action) => {
            console.log('[Session] Ended', action);
            this.configureNewRelicAttributes(action.session, true);
        })), { dispatch: false });
        this.clearTokenState$ = createEffect(() => this.actions$.pipe(ofType(TOKEN_ACTIONS.clearTokenState), tap(() => this.webSocketService.closeConnection())), { dispatch: false });
    }
    getLoggedInUserFromToken(accessToken) {
        const payload = jwtDecode(accessToken);
        const user = payload['usf-claims'];
        return {
            userId: user.userName.replace('[AD_AUTO]', ''),
            ecomUserId: user.userId,
            tmUser: user.tmUser,
        };
    }
    configureNewRelicAttributes(session, sessionEnd = false) {
        const newrelic = window['newrelic'];
        if (!!newrelic && !!session) {
            const userName = sessionEnd
                ? null
                : session.userName?.replace('[AD_AUTO]', '');
            const userId = sessionEnd ? null : session.ecomUserId;
            const customer = sessionEnd ? null : session.selectedCustomerId;
            const division = sessionEnd ? null : session.division;
            const department = sessionEnd ? null : session.department;
            const clientBuildNumber = sessionEnd ? null : this.config.buildNumber;
            newrelic.setCustomAttribute(AgentAttributes.userName, userName);
            newrelic.setCustomAttribute(AgentAttributes.userId, userId);
            newrelic.setCustomAttribute(AgentAttributes.customerNumber, customer);
            newrelic.setCustomAttribute(AgentAttributes.division, division);
            newrelic.setCustomAttribute(AgentAttributes.department, department);
            newrelic.setCustomAttribute(AgentAttributes.clientBuildNumber, clientBuildNumber);
            newrelic.setUserId(userName);
        }
        else {
            console.log('Attempting to configure agent failed.');
        }
    }
}
PlatformEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PlatformEffects, deps: [{ token: i1$5.Actions }, { token: i2$2.WebSocketService }, { token: UsfTokenService }, { token: PanAppState }, { token: PlatformInitializationService }, { token: PAN_APP_STATE_LIB_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
PlatformEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PlatformEffects, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PlatformEffects, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$5.Actions }, { type: i2$2.WebSocketService }, { type: UsfTokenService }, { type: PanAppState }, { type: PlatformInitializationService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [PAN_APP_STATE_LIB_CONFIG]
                }] }]; } });

class TokenEffects {
    constructor(panAppStateConfig, actions$, webSocketService) {
        this.panAppStateConfig = panAppStateConfig;
        this.actions$ = actions$;
        this.webSocketService = webSocketService;
        this.loadTokenSuccess$ = createEffect(() => this.actions$.pipe(ofType(TOKEN_ACTIONS.loadTokenSuccess), map((action) => {
            return action.loginResponse;
        }), concatMap((loginResponse) => {
            return [
                USER_ACTIONS.loadUserSuccess({
                    loggedInUser: this.getLoggedInUserFromToken(loginResponse.accessToken),
                }),
                CUSTOMER_ACTIONS.loadSelectedCustomerSuccess({
                    selectedCustomer: {
                        customerNumber: loginResponse.customerNumber,
                        divisionNumber: loginResponse.divisionNumber,
                        departmentNumber: loginResponse.departmentNumber,
                    },
                }),
            ];
        }), catchError((e) => of(TOKEN_ACTIONS.loadTokenFail()))));
        this.clearTokenState$ = createEffect(() => this.actions$.pipe(ofType(TOKEN_ACTIONS.clearTokenState), tap((action) => this.webSocketService.closeConnection())), { dispatch: false });
    }
    getLoggedInUserFromToken(accessToken) {
        const payload = jwtDecode(accessToken);
        const user = payload['usf-claims'];
        return {
            userId: user.userName.replace('[AD_AUTO]', ''),
            ecomUserId: user.userId,
            tmUser: user.tmUser,
        };
    }
}
TokenEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: TokenEffects, deps: [{ token: PAN_APP_STATE_LIB_CONFIG }, { token: i1$5.Actions }, { token: i2$2.WebSocketService }], target: i0.ɵɵFactoryTarget.Injectable });
TokenEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: TokenEffects, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: TokenEffects, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [PAN_APP_STATE_LIB_CONFIG]
                }] }, { type: i1$5.Actions }, { type: i2$2.WebSocketService }]; } });

class MinimumVersionsEffects {
    constructor(actions$, httpClient, config) {
        this.actions$ = actions$;
        this.httpClient = httpClient;
        this.config = config;
        this.minimumVersions$ = createEffect(() => this.actions$.pipe(ofType(MINIMUM_VERSIONS_ACTIONS.getMinimumVersions), concatMap(() => this.httpClient
            .get(`${this.config.minimumVersionsConfigApiUrl}`)
            .pipe(map((minimumVersions) => MINIMUM_VERSIONS_ACTIONS.setMinimumVersions({
            minimumVersions,
        })), catchError((minimumVersionsError) => of(MINIMUM_VERSIONS_ACTIONS.getMinimumVersionsFail({
            minimumVersionsError,
        })))))));
    }
}
MinimumVersionsEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MinimumVersionsEffects, deps: [{ token: i1$5.Actions }, { token: i1$4.HttpClient }, { token: PAN_APP_STATE_LIB_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
MinimumVersionsEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MinimumVersionsEffects, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MinimumVersionsEffects, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$5.Actions }, { type: i1$4.HttpClient }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [PAN_APP_STATE_LIB_CONFIG]
                }] }]; } });

class PanAppStateModule {
    constructor(panAppStateLibConfig, webSocketService) {
        this.panAppStateLibConfig = panAppStateLibConfig;
        this.webSocketService = webSocketService;
    }
    static forRoot(panAppStateLibConfig, skipInitialization = () => false) {
        return {
            ngModule: PanAppStateModule,
            providers: [
                PanAppState,
                {
                    provide: PAN_APP_STATE_LIB_CONFIG,
                    useValue: panAppStateLibConfig,
                },
                {
                    provide: APP_INITIALIZER,
                    useFactory: initializePlatform,
                    deps: [PlatformInitializationService, 'skipInitialization'],
                    multi: true,
                },
                {
                    provide: 'skipInitialization',
                    useValue: skipInitialization,
                },
            ],
        };
    }
}
PanAppStateModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PanAppStateModule, deps: [{ token: PAN_APP_STATE_LIB_CONFIG }, { token: i2$2.WebSocketService, optional: true }], target: i0.ɵɵFactoryTarget.NgModule });
PanAppStateModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: PanAppStateModule, imports: [i2.StoreFeatureModule, i1$5.EffectsFeatureModule] });
PanAppStateModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PanAppStateModule, imports: [StoreModule.forFeature(APP_STATE_KEY, appStateReducer),
        EffectsModule.forFeature([
            TokenEffects,
            PlatformEffects,
            MinimumVersionsEffects,
        ])] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PanAppStateModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [
                        StoreModule.forFeature(APP_STATE_KEY, appStateReducer),
                        EffectsModule.forFeature([
                            TokenEffects,
                            PlatformEffects,
                            MinimumVersionsEffects,
                        ]),
                    ],
                    exports: [],
                    providers: [],
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [PAN_APP_STATE_LIB_CONFIG]
                }] }, { type: i2$2.WebSocketService, decorators: [{
                    type: Optional
                }] }]; } });
function initializePlatform(platformInitializationService, skipInitialization) {
    if (skipInitialization()) {
        console.log('Skipping platform initialization...');
        return () => Promise.resolve();
    }
    return () => platformInitializationService.initialize();
}

class TokenModule {
    constructor(panAppStateLibConfig) {
        this.panAppStateLibConfig = panAppStateLibConfig;
    }
    static forRoot(panAppStateLibconfig) {
        return {
            ngModule: TokenModule,
            providers: [
                {
                    provide: PAN_APP_STATE_LIB_CONFIG,
                    useValue: panAppStateLibconfig,
                },
                UsfTokenService,
                UsfTokenStorageService,
            ],
        };
    }
}
TokenModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: TokenModule, deps: [{ token: PAN_APP_STATE_LIB_CONFIG }], target: i0.ɵɵFactoryTarget.NgModule });
TokenModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: TokenModule });
TokenModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: TokenModule, providers: [
        UsfTokenService,
        UsfTokenClient,
        UsfTokenStorageService,
        UsfTokenValidator,
    ] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: TokenModule, decorators: [{
            type: NgModule,
            args: [{
                    providers: [
                        UsfTokenService,
                        UsfTokenClient,
                        UsfTokenStorageService,
                        UsfTokenValidator,
                    ],
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [PAN_APP_STATE_LIB_CONFIG]
                }] }]; } });

class HttpTraceInterceptor {
    constructor(config, tracingService) {
        this.config = config;
        this.tracingService = tracingService;
        // TRACE CONTEXT MAP
        // should have key of url string
        // should have value that is the trace context string
        // ex)
        //{
        //  'http://google.com': 'search'
        // }
        this.traceContextMap = {};
        this.traceContextMap = config.openTelemetrySettings.traceContextMap || {};
    }
    intercept(req, next) {
        //OPTL logic
        if (this.traceContextMap[req.url] &&
            this.tracingService.parentSpans[this.traceContextMap[req.url]]) {
            const parent = this.tracingService.parentSpans[this.traceContextMap[req.url]] || null;
            let span = null;
            if (req.headers.get('trace-context')) {
                span = this.tracingService.initChildHttpSpan(req, this.tracingService.parentSpans[req.headers.get('trace-context')]);
            }
            else {
                span = this.tracingService.initChildHttpSpan(req, this.tracingService.parentSpans[this.traceContextMap[req.url]]);
            }
            const tracedReq = this.tracingService.injectContextAndHeader(req, span);
            return next.handle(tracedReq).pipe(tap((event) => {
                span.setAttributes({
                    'http.status_code': event.status,
                    'http.status_text': event.statusText,
                });
                // if (event.body != null) {
                //   span.addEvent('response', { body: JSON.stringify(event.body) });
                // }
                span.setStatus({
                    code: SpanStatusCode.UNSET,
                });
                this.tracingService.setCustomSpan(span, req, event);
            }, (event) => {
                span.setAttributes({
                    'http.status_text': event.statusText,
                    'http.status_code': event.status,
                });
                span.recordException({
                    name: event.name,
                    message: event.message,
                    stack: event.error,
                });
                span.setStatus({
                    code: SpanStatusCode.ERROR,
                });
                this.tracingService.setCustomSpan(span, req, event);
            }), finalize(() => {
                span.end();
                this.tracingService.contextManager.disable();
            }));
        }
        else {
            return next.handle(req);
        }
    }
}
HttpTraceInterceptor.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: HttpTraceInterceptor, deps: [{ token: PAN_APP_STATE_LIB_CONFIG }, { token: TracingService }], target: i0.ɵɵFactoryTarget.Injectable });
HttpTraceInterceptor.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: HttpTraceInterceptor });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: HttpTraceInterceptor, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [PAN_APP_STATE_LIB_CONFIG]
                }] }, { type: TracingService }]; } });

/*
 * Public API Surface of pan-app-state
 */

/**
 * Generated bundle index. Do not edit.
 */

export { APP_STATE_KEY, AgentAttributes, AuthConnectService, CUSTOMER_ACTIONS, Customizations, FEATURE_FLAG_ACTIONS, HttpTraceInterceptor, LOCAL_STORAGE_KEYS, MINIMUM_VERSIONS_ACTIONS, MockActivatedRoute, MockRouter, NETWORK_ACTIONS, OPEN_TELEMETRY_INJECT_CONFIG, PAGE_ACTIONS, PAN_APP_STATE_LIB_CONFIG, PLATFORM_ACTIONS, PREFERENCES_KEYS, PageInfoService, PanAppState, PanAppStateModule, PlatformEnum, PlatformService, SESSION_STORAGE_KEYS, TOKEN_ACTIONS, TokenAuthMode, TokenModule, TracingService, TrackingService, USER_ACTIONS, UsabillaService, UsfTokenService, UsfTokenStorageService, appStateReducer, applicationTransform, customerTransform, defaultAnalyticsTransform, defaultApiRetryConditions, defaultApiRetryStrategy, defaultLoggingTransform, defaultTracingTransform, getReferenceFromString, handleUnitTestError, isTokenExpired, jwtDecode, languageSettingTransform, loggingDataTransform, loggingPageTransform, makeAsyncSafe, navigationTransform, pageTrackingTransform, platformTransform, previousPageTransform, retryBackoff, searchTransform, selectAccessToken, selectAllFeaturesEnabled, selectBackgroundTime, selectCurrentUrl, selectCustomer, selectLoggedInUser, selectLogrocketUrl, selectMinimumVersions, selectMinimumVersionsLoaded, selectOnline, selectPageTitle, selectPlatform, selectPlatformType, selectPreviousUrl, selectRefreshToken, selectSession, selectShowInventory, selectToken, selectTokenExpiration, tracingPageTransform, tracker, urlOrigin, usabillaTransform, userLoggingTransform, userTracingTransform, userTransform };
